/* eslint-disable no-undef, react/jsx-no-undef, no-loop-func */
import React, { useEffect, useRef, useState } from 'react';
import './ChatInterface.css';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS, BACKEND_URL } from '../config/api';
import { useSpeechmaticsRecorder } from '../hooks/useSpeechmaticsRecorder';
import { fetchCreditsCached, clearCreditsCache } from '../services/credits';

function ChatInterface() {
  const RESUME_TEXT_CACHE_KEY = 'resume_text_cache';
  const LEGACY_RESUME_KEY = 'resume_text_response';
  
  // Get language from interview config
  const interviewConfig = JSON.parse(localStorage.getItem('interview_config') || '{}');
  const selectedLanguage = interviewConfig.language || 'en';
  
  // Use Speechmatics recorder with selected language
  // sessionId and email are read from localStorage at connect() time inside the hook
  const {
    isRecording: isSpeechmaticsRecording,
    transcript: speechmaticsTranscript,
    error: speechmaticsError,
    isConnected: _speechmaticsConnected, // eslint-disable-line no-unused-vars
    captureSystemAudio: _captureSystemAudio, // eslint-disable-line no-unused-vars
    setCaptureSystemAudio,
    useExternalAudio,
    setUseExternalAudio,
    startRecording: startSpeechmaticsRecording,
    stopRecording: stopSpeechmaticsRecording,
    clearTranscript: clearSpeechmaticsTranscript
  } = useSpeechmaticsRecorder(selectedLanguage);
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [chatResponse, _setChatResponse] = useState(''); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoScroll, _setAutoScroll] = useState(true); // eslint-disable-line no-unused-vars
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text}
  const [typed, setTyped] = useState('');
  const [screenStream, setScreenStream] = useState(null);
  const [analyzingScreen, setAnalyzingScreen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes (600 seconds)
  const [sessionActive, setSessionActive] = useState(true);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showNoCreditModal, setShowNoCreditModal] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [cachedResumeData, setCachedResumeData] = useState(null);
  const [show5MinWarning, setShow5MinWarning] = useState(false);
  const [show30SecWarning, setShow30SecWarning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed time from 0
  const [showLowCreditWarning, setShowLowCreditWarning] = useState(false); // Warning when only 30 mins left
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [exitIntent, setExitIntent] = useState(null); // reload | browser-close | manual
  const [maxSessionDuration, setMaxSessionDuration] = useState(null); // Max session time in seconds based on credits
  const [showMaxTimeWarning, setShowMaxTimeWarning] = useState(false); // Warning 5 min before max time
  const [isMaxTimeBlinking, setIsMaxTimeBlinking] = useState(false); // Blink animation window for max time warning
  const [currentSessionId, setCurrentSessionId] = useState(null); // Track current session ID from API

  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const videoRef = useRef(null);
  const autoStartTriggeredRef = useRef(false);
  const preventTabSwitchRef = useRef(false);
  const timerIntervalRef = useRef(null);
  const sendingRequestRef = useRef(false); // Prevent duplicate requests
  const isRecordingRef = useRef(false); // Track recording state for closures
  const recognitionErrorRef = useRef(false); // Track if error occurred
  const restartTimeoutRef = useRef(null); // Track restart timeout
  const resumeFetchInFlightRef = useRef(false); // Prevent duplicate resume fetches
  const lastResumeIdFetchedRef = useRef(null); // Track last fetched resumeId
  const creditDeductionIntervalRef = useRef(null); // Track 30-minute credit deduction interval
  const creditsDeductedCountRef = useRef(0); // Track how many times credits have been deducted
  const totalCreditsUsedRef = useRef(0); // Track total credits already recorded for this session
  const isDeductingCreditsRef = useRef(false); // Prevent duplicate credit deductions
  const lowCreditWarningShownRef = useRef(false); // Track if low credit warning has been shown
  const lastDeductionTimeRef = useRef(null); // Track when credits were last deducted
  const elapsedTimerRef = useRef(null); // Track elapsed time interval
  const insertGuardRef = useRef(false); // Prevent rapid duplicate /sessions/insert calls
  const allowUnloadRef = useRef(false); // Allow one unblock when user confirms exit
  const hasSentUnloadExitRef = useRef(false); // Ensure unload keepalive fires once
  const isExitingRef = useRef(false); // Prevent double exit
  const maxDurationExitTriggeredRef = useRef(false); // Prevent duplicate max-duration exits
  const maxTimeWarningTimeoutRef = useRef(null); // Hide max-time warning after display window
  const maxTimeBlinkTimeoutRef = useRef(null); // Stop blinking after initial period
  const heartbeatIntervalRef = useRef(null); // Track 3-minute heartbeat interval
  const speechmaticsActiveRef = useRef(false); // Track if Speechmatics is providing transcription
  const chatListRef = useRef(null); // Right panel scroll container
  const messageRefs = useRef([]); // Individual message anchors for question-to-question scrolling
  const lastAssistantIndexScrolledRef = useRef(-1); // Avoid repeated auto-scroll while user reads
  const navigate = useNavigate();

  // Normalize model name to avoid unsupported GPT-5.* selections
  const normalizeModel = (modelName) => {
    if (!modelName) return 'gpt-4o';
    const lower = modelName.toLowerCase();
    if (lower.includes('gpt-5')) return 'gpt-4o';
    return modelName;
  };

  // Function to bring focus back to the current window/tab
  const bringBackFocus = () => {
    try {
      // Multiple aggressive focus attempts
      if (window.focus) {
        window.focus();
      }
      // Try to focus on the document body
      if (document.body && document.body.focus) {
        document.body.focus();
      }
      // Try to blur and refocus
      if (window.blur) {
        window.blur();
        setTimeout(() => {
          if (window.focus) window.focus();
        }, 0);
      }
      // Trigger a click event to ensure window activation
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      document.body?.dispatchEvent(clickEvent);
    } catch (err) {
      console.error('Error bringing focus back:', err);
    }
  };

  // Handle visibility changes to prevent tab switching during screen share
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (preventTabSwitchRef.current && document.hidden) {
      // console.log('Tab lost focus, attempting to refocus...');
        setTimeout(() => {
          bringBackFocus();
        }, 50);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Cleanup max-time warning timers on unmount
  useEffect(() => () => {
    if (maxTimeWarningTimeoutRef.current) {
      clearTimeout(maxTimeWarningTimeoutRef.current);
    }
    if (maxTimeBlinkTimeoutRef.current) {
      clearTimeout(maxTimeBlinkTimeoutRef.current);
    }
  }, []);

  // Elapsed time counter - counts up from 0
  useEffect(() => {
    if (sessionActive && sessionStartTime) {
      elapsedTimerRef.current = setInterval(() => {
        setElapsedTime(prev => {
          const newTime = prev + 1;
          
          // Check if we're 5 minutes before max time (non-trial sessions only)
          if (!isTrial && maxSessionDuration && newTime === maxSessionDuration - 300 && !hasShownWarning) {
      // console.log('⚠️ Warning: Session will end in 5 minutes (max time limit)');
            setShowMaxTimeWarning(true);
            setIsMaxTimeBlinking(true);
            if (maxTimeBlinkTimeoutRef.current) {
              clearTimeout(maxTimeBlinkTimeoutRef.current);
            }
            if (maxTimeWarningTimeoutRef.current) {
              clearTimeout(maxTimeWarningTimeoutRef.current);
            }
            maxTimeBlinkTimeoutRef.current = setTimeout(() => setIsMaxTimeBlinking(false), 3000); // Blink for 3 seconds
            maxTimeWarningTimeoutRef.current = setTimeout(() => setShowMaxTimeWarning(false), 10000); // Visible for 10s total (3s blink + 7s static)
            setHasShownWarning(true);
          }

          // Hard stop at available-credit limit
          if (!isTrial && maxSessionDuration && newTime >= maxSessionDuration && !maxDurationExitTriggeredRef.current) {
      // console.log('⏹ Max session duration reached based on credits; exiting.');
            handleMaxDurationAutoExit();
          }
          
          return newTime;
        });
      }, 1000);

      localStorage.setItem('session_active', 'true');
    }

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, sessionStartTime, maxSessionDuration, isTrial, hasShownWarning]);

  // Countdown timer for trial session (original logic for trial)
  useEffect(() => {
    if (sessionActive && isTrial && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            handleSessionEnd(); // eslint-disable-line react-hooks/exhaustive-deps
            return 0;
          }
          // Show warning at 5 minutes (300 seconds) remaining
          if (prev === 300) {
            setShow5MinWarning(true);
      // console.log('⚠️ Warning: Your session will end in 5 minutes');
            // Hide after 10 seconds (3 sec blink + 7 sec static)
            setTimeout(() => {
              setShow5MinWarning(false);
            }, 10000);
          }
          // Show warning at 30 seconds remaining (keep visible)
          if (prev === 30) {
            setShow30SecWarning(true);
      // console.log('⚠️ Warning: Your session will be ending soon (30 seconds remaining)');
          }
          return prev - 1;
        });
      }, 1000);

      // Store session start time and end time
      const endTime = Date.now() + (timeRemaining * 1000);
      localStorage.setItem('session_end_time', endTime.toString());
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [sessionActive, isTrial, timeRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up 30-minute interval for credit deduction (non-trial sessions only)
  useEffect(() => {
    // Only set up interval for non-trial sessions with active session AND after we have sessionId
    if (!isTrial && sessionActive && sessionStartTime && currentSessionId) {
      // console.log('✅ Setting up 30-minute credit deduction interval for session', currentSessionId);
      
      // Set up interval to deduct credits every 30 minutes (after 30 minutes from start)
      creditDeductionIntervalRef.current = setInterval(() => {
      // console.log('⏰ 30-minute interval reached, deducting credits...');
        deductCredits();
      }, 30 * 60 * 1000); // 30 minutes in milliseconds
    }

    // Cleanup interval on unmount or when session ends
    return () => {
      if (creditDeductionIntervalRef.current) {
      // console.log('Clearing credit deduction interval');
        clearInterval(creditDeductionIntervalRef.current);
        creditDeductionIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, sessionStartTime, isTrial, currentSessionId]);

  // Intercept browser reload/close to show custom warning
  useEffect(() => {
    if (!sessionActive || isTrial || !sessionStartTime) return undefined;

    const handleBeforeUnload = (event) => {

      if (allowUnloadRef.current) {

        return;
      }

      // Fire a keepalive exit so credits are deducted even if the tab actually closes
      // This ensures session termination even if user forces refresh
      sendKeepAliveExit('browser-close');

      // Use ONLY the native browser confirmation dialog
      // Don't try to show React modal here - it won't render in time
      event.preventDefault();
      event.returnValue = '⚠️ Your session will end and credits will be deducted. Continue?';
      return '⚠️ Your session will end and credits will be deducted. Continue?';
    };

    const handleReloadKeys = (e) => {
      if (allowUnloadRef.current) return;
      const isReloadKey = e.key === 'F5' || (e.key?.toLowerCase() === 'r' && (e.ctrlKey || e.metaKey));
      if (isReloadKey) {
        // CRITICAL: Prevent default FIRST, before any other logic
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Show our custom modal for keyboard shortcuts
        setExitIntent('reload');
        setShowExitConfirm(true);
        
        // Return false to ensure prevention
        return false;
      }
    };

    // pagehide is more reliable than unload on modern browsers (especially mobile Safari and Chrome)
    // This is a final safety net if user somehow bypasses the confirmation
    const handlePageHide = (event) => {
      if (allowUnloadRef.current) return;
      
      // Only send if we haven't already sent via beforeunload
      if (!hasSentUnloadExitRef.current) {
        sendKeepAliveExit('pagehide');
      }
    };

    // Attach to both window and document for maximum coverage
    // Use capture phase for keyboard events to intercept before any other handlers
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('keydown', handleReloadKeys, true); // Capture phase on document
    window.addEventListener('keydown', handleReloadKeys, true); // Capture phase on window
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('keydown', handleReloadKeys, true);
      window.removeEventListener('keydown', handleReloadKeys, true);
      window.removeEventListener('pagehide', handlePageHide);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, isTrial, sessionStartTime]);

  // Keyboard shortcut: Delete key to clear transcript (like the ✕ Clear button)
  useEffect(() => {
    const handleClearShortcut = (e) => {
      // Skip if user is typing in an input/textarea/contentEditable
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

      if (e.key === 'Delete') {
        e.preventDefault();
        clearSpeechmaticsTranscript();
        finalTranscriptRef.current = '';
        setTranscript('');
      }
    };

    document.addEventListener('keydown', handleClearShortcut);
    return () => document.removeEventListener('keydown', handleClearShortcut);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intercept browser back button
  useEffect(() => {
    if (!sessionActive || isTrial || !sessionStartTime) return undefined;

    // Push a dummy state to history so we can intercept back button
    window.history.pushState({ chatInterface: true }, '', window.location.href);
    
    const handlePopState = (event) => {
      if (allowUnloadRef.current) {
        // User already confirmed exit, allow navigation
        return;
      }

      // Prevent default back navigation
      event.preventDefault();

      // Push state back to keep user on the page
      window.history.pushState({ chatInterface: true }, '', window.location.href);
      
      // Show exit confirmation modal
      setExitIntent('back-button');
      setShowExitConfirm(true);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [sessionActive, isTrial, sessionStartTime]);

  // Deduct credits every 30 minutes during the session
  const deductCredits = async () => {
    // Prevent duplicate deductions
    if (isDeductingCreditsRef.current) {
      return;
    }

    // Skip for trial sessions
    if (isTrial) {
      return;
    }

    try {
      isDeductingCreditsRef.current = true;

      // Get user email
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown@email.com';

      if (!isAuthenticated()) {
        console.error('No authentication found for credit deduction');
        return;
      }

      // Check current credits before deducting (cached fetch to avoid duplicate calls)
      // console.log('📊 Fetching current credits...');
      const creditsData = await fetchCreditsCached();
      if (creditsData) {
        const currentCredits = creditsData.totalCredits || 0;
        setUserCredits(currentCredits);

      // console.log('💰 Current credits before deduction:', currentCredits);

        // Check if user has enough credits
        if (currentCredits < 0.5) {
      // console.log('❌ Insufficient credits (< 0.5), stopping deduction and showing modal');
          setShowNoCreditModal(true);
          isDeductingCreditsRef.current = false; // Reset guard
          // Do not auto-exit; allow the user to decide next steps even if credits are low.
          return;
        }
        
      // console.log('✅ Credits check passed - user has', currentCredits, 'credits (>= 0.5)');

        // Show warning if user will have only 0.5 credits left after this deduction
        if (currentCredits === 1.0 && !lowCreditWarningShownRef.current) {
      // console.log('⚠️ Warning: After this deduction, only 30 minutes (0.5 credits) will remain');
          setShowLowCreditWarning(true);
          lowCreditWarningShownRef.current = true;
          // Hide warning after 10 seconds
          setTimeout(() => {
            setShowLowCreditWarning(false);
          }, 10000);
        }
      }

      const sessionData = JSON.parse(localStorage.getItem('interview_config') || '{}');
      
      // Calculate incremental duration since last deduction (or from start if first deduction)
      const fromLastDeduction = lastDeductionTimeRef.current !== null;
      let durationSeconds, duration;
      
      // For the first 30-minute interval deduction, use exactly 1800 seconds
      // to avoid timing drift (e.g., 1801 seconds due to JavaScript timing)
      if (!fromLastDeduction && creditsDeductedCountRef.current === 0) {
        durationSeconds = 1800; // Exactly 30 minutes
        duration = formatDuration(1800);
      // console.log('First 30-minute interval: using exact 1800 seconds');
      } else {
        const result = getSessionDuration(null, fromLastDeduction);
        durationSeconds = result.durationSeconds;
        duration = result.duration;
      }
      
      // For interval deductions, calculate credits directly from incremental duration
      // No need to subtract totalCreditsUsedRef since duration is already incremental
      const creditsForThisInterval = Number((Math.max(0, durationSeconds) / 3600).toFixed(4));

      if (creditsForThisInterval <= 0) {
      // console.log('No pending credits to deduct at this interval');
        return;
      }

      creditsDeductedCountRef.current += 1;

      const payload = {
        company: sessionData.company || 'Unknown Company',
        jobDescription: sessionData.jobDescription || 'Unknown Position',
        duration,
        createdAt: sessionStartTime.toISOString(),
        language: sessionData.language || 'en',
        aiModel: sessionData.aiModel || 'gpt-4o'
      };
      
      // Always add sessionId for interval calls (required to update existing session)
      if (currentSessionId) {
        payload.sessionId = currentSessionId;
      }

      const headers = getAuthHeaders();

      // Call API to deduct credits - cookie sent automatically
      const response = await fetch(API_ENDPOINTS.SESSIONS.INSERT(userEmail), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Credit deduction API error:', errorText);
        
        // If no credits left, show modal and exit
        if (response.status === 400 && errorText.includes('insufficient credits')) {
      // console.log('Insufficient credits, ending session');
          setShowNoCreditModal(true);
          // Do not auto-exit on insufficient credits; let the user exit manually.
        }
      } else {
        await response.json();
      clearCreditsCache();
        
        // Update last deduction timestamp
        lastDeductionTimeRef.current = new Date();
        
        // Update local credits state
        if (userCredits !== null) {
          setUserCredits(prev => Math.max(0, prev - creditsForThisInterval));
        }
        totalCreditsUsedRef.current += creditsForThisInterval;
        
        // Check remaining credits after deduction
        await checkCreditsForActualSession();
      }
    } catch (error) {
      console.error('Error deducting credits:', error);
    } finally {
      isDeductingCreditsRef.current = false;
    }
  };

  // Check credits and show warning if needed
  const checkCreditsAndShowWarning = async () => { // eslint-disable-line no-unused-vars
    try {
      const data = await fetchCreditsCached();
      const totalCredits = data?.totalCredits || 0;
      // Show warning if credits are low or 0
      if (totalCredits === 0) {
        setShowWarningModal(true);
      }
    } catch (error) {
      console.error('Error checking credits:', error);
    }
  };

  // Check credits for actual sessions and force exit if depleted
  const checkCreditsForActualSession = async () => {
    try {
      const data = await fetchCreditsCached();
      const totalCredits = data?.totalCredits || 0;
      setUserCredits(totalCredits);
      
      // Force exit if no credits left in actual session
      if (totalCredits < 0.5 && !isTrial) {
      // console.log('No credits remaining; showing warning without forcing exit');
        setShowNoCreditModal(true);
      }
    } catch (error) {
      console.error('Error checking credits:', error);
    }
  };

  // Initialize session by calling insert API
  const insertSession = async () => {
    // Prevent duplicate insert calls
    if (insertGuardRef.current) {
      return;
    }
    
    insertGuardRef.current = true;
    
    try {
      if (!isAuthenticated()) {
        console.warn('No auth for session insert');
        insertGuardRef.current = false;
        return;
      }

      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown@email.com';
      const sessionData = JSON.parse(localStorage.getItem('interview_config') || '{}');
      
      const payload = {
        company: sessionData.company || 'Unknown Company',
        jobDescription: sessionData.jobDescription || 'Unknown Position',
        createdAt: new Date().toISOString(),
        language: sessionData.language || 'en',
        aiModel: sessionData.aiModel || 'gpt-4o',
        trial: isTrial
      };

      const headers = getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.SESSIONS.INSERT(userEmail), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Session insert error:', errorText);
        insertGuardRef.current = false; // Reset guard on error so user can retry
        return;
      }

      const result = await response.json();
      
      // Try different possible field names for session ID
      const sessionId = result.sessionId || result.session_id || result.id || result.insertId;
      
      if (sessionId) {
        setCurrentSessionId(sessionId);
        // Store in localStorage as backup
        localStorage.setItem('current_session_id', sessionId);
      }
      
      // Reset guard after successful insert so exit operations can proceed
      setTimeout(() => {
        insertGuardRef.current = false;
      // console.log('✅ Insert guard reset - exit operations now allowed');
      }, 1000);
      
      return result.sessionId;
    } catch (error) {
      console.error('Error initializing session:', error);
      insertGuardRef.current = false; // Reset guard on error so user can retry
    }
  };

  // Send heartbeat to keep session alive
  const sessionHeartbeat = async () => {
    if (!currentSessionId) {
      console.warn('No sessionId for heartbeat');
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown@email.com';
      
      const headers = getAuthHeaders();

      const response = await fetch(`${BACKEND_URL}/api/sessions/heartbeat?email=${encodeURIComponent(userEmail)}&sessionId=${currentSessionId}`, {
        method: 'POST',
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Heartbeat error:', errorText);
        return;
      }

      await response.json();
    } catch (error) {
      console.error('Error sending heartbeat:', error);
    }
  };

  // Handle session end
  const handleSessionEnd = () => {
    setSessionActive(false);
    setShowEndModal(true);
    
    // Clear credit deduction interval
    if (creditDeductionIntervalRef.current) {
      clearInterval(creditDeductionIntervalRef.current);
      creditDeductionIntervalRef.current = null;
    }
    
    // Clear elapsed time interval
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    
    // Clear heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    // Stop recording if active
    if (isRecording) {
      stopRecording();
    }
    
    // Set cooldown period (1 minute) - only for paid sessions, not trial
    if (!isTrial) {
      const cooldownEnd = Date.now() + (60 * 1000); // 1 minute from now
      localStorage.setItem('cooldown_end', cooldownEnd.toString());
    } else {
      // For trial sessions, record when trial ENDED (cooldown starts from end, not start)
      localStorage.setItem('bk_last_trial_ended_at', String(Date.now()));
    }
    localStorage.removeItem('session_active');
    localStorage.removeItem('session_end_time');
  };

  // Auto exit when max allotted duration is reached
  const handleMaxDurationAutoExit = async () => {
    if (isTrial) return;
    if (maxDurationExitTriggeredRef.current) return;
    if (!maxSessionDuration) return;

    maxDurationExitTriggeredRef.current = true;
    setSessionActive(false);
    setShowMaxTimeWarning(false);

    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }

    if (creditDeductionIntervalRef.current) {
      clearInterval(creditDeductionIntervalRef.current);
      creditDeductionIntervalRef.current = null;
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    try {
      await exitSession({ durationOverrideSeconds: maxSessionDuration, reason: 'max-duration' });
    } catch (err) {
      console.error('Auto-exit at max duration failed', err);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (durationSeconds) => {
    const safeSeconds = Math.max(0, Math.floor(durationSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getSessionDuration = (overrideSeconds = null, fromLastDeduction = false) => {
    const now = new Date();
    let durationMs;
    
    if (fromLastDeduction && lastDeductionTimeRef.current) {
      // Calculate duration since last credit deduction
      durationMs = now - lastDeductionTimeRef.current;
    } else {
      // Calculate total duration from session start
      durationMs = sessionStartTime ? now - sessionStartTime : 0;
    }
    
    const computedSeconds = Math.floor(durationMs / 1000);
    const durationSeconds = overrideSeconds != null ? Math.max(0, Math.floor(overrideSeconds)) : Math.max(0, computedSeconds);
    return {
      durationSeconds,
      duration: formatDuration(durationSeconds)
    };
  };

  const calculatePendingCredits = (durationSeconds) => {
    const totalCreditsRequired = Number((Math.max(0, durationSeconds) / 3600).toFixed(4));
    const pendingCredits = Math.max(0, Number((totalCreditsRequired - totalCreditsUsedRef.current).toFixed(4)));
    return { pendingCredits, totalCreditsRequired };
  };

  const buildExitPayload = (durationOverrideSeconds = null) => {
    // Calculate incremental duration since last deduction (or from start if no deductions yet)
    const fromLastDeduction = lastDeductionTimeRef.current !== null;
    const { durationSeconds, duration } = getSessionDuration(durationOverrideSeconds, fromLastDeduction);
    if (!isTrial) {
      calculatePendingCredits(durationSeconds);
    }

    const sessionData = JSON.parse(localStorage.getItem('interview_config') || '{}');
    const payload = {
      company: sessionData.company || 'Unknown Company',
      jobDescription: sessionData.jobDescription || 'Unknown Position',
      duration,
      createdAt: sessionStartTime?.toISOString?.() || new Date().toISOString(),
      language: sessionData.language || 'en',
      aiModel: sessionData.aiModel || 'gpt-4o',
      trial: isTrial
    };

    return { payload };
  };

  const sendKeepAliveExit = (intent) => {

    if (hasSentUnloadExitRef.current) {

      return;
    }
    
    // Only set this flag for actual unload events, not for user confirmations
    if (intent !== 'reload-confirmed' && intent !== 'back-button-confirmed') {
      hasSentUnloadExitRef.current = true;
    }

    // Don't use insertGuard for keepalive exits - these are time-critical

    try {
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown@email.com';

      if (!isAuthenticated() || !sessionStartTime) {
        console.error('❌ Missing required data - auth:', isAuthenticated(), 'sessionStartTime:', !!sessionStartTime);
        return;
      }

      const { payload } = buildExitPayload();
      
      // Add session termination fields - critical for credit deduction
      payload.status = 'COMPLETED';
      payload.sessionTerminate = true;
      payload.trial = isTrial;
      
      // Try to get sessionId from multiple sources
      const storedSessionId = localStorage.getItem('current_session_id');
      const finalSessionId = currentSessionId || storedSessionId;
      
      if (finalSessionId) {
        payload.sessionId = parseInt(finalSessionId, 10);
      }

      const baseUrl = API_ENDPOINTS.SESSIONS.INSERT(userEmail);
      const body = JSON.stringify(payload);

      const headers = getAuthHeaders();

      // Use fetch with keepalive for most reliable delivery
      // This works better than sendBeacon in practice for authenticated requests
      fetch(baseUrl, {
        method: 'POST',
        headers,
        credentials: 'include', // Send cookies for cookie-based auth
        body: body,
        keepalive: true // Critical: allows request to complete even if page unloads
      }).then(response => {

        return response.json();
      }).then(data => {

        // Dispatch event to refresh credits
        window.dispatchEvent(new CustomEvent('creditsUpdated'));
      }).catch((err) => {
        console.error('❌ Failed to send session termination:', err);
        
        // Try sendBeacon as fallback if fetch fails
        if (navigator.sendBeacon) {
          const urlWithToken = `${baseUrl}&token=${encodeURIComponent(token)}`;
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(urlWithToken, blob);

        }
      });
    } catch (err) {
      console.error('keepalive exit failed', err);
    }
  };

  // Exit session and call API to deduct credits
  const exitSession = async (options = {}) => {
    const { durationOverrideSeconds = null } = options;
    
    if (isExitingRef.current) {
      return;
    }
    isExitingRef.current = true;

    try {
      if (!isAuthenticated()) {
        throw new Error('No authentication found');
      }

      // Prevent rapid duplicate inserts (e.g., exit triggered twice)
      if (insertGuardRef.current) {
        return;
      }
      insertGuardRef.current = true;
      setTimeout(() => {
        insertGuardRef.current = false;
      }, 2000);

      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown@email.com';
      const { payload } = buildExitPayload(durationOverrideSeconds);

      // Add session termination fields
      payload.status = 'COMPLETED';
      payload.sessionTerminate = true;
      payload.trial = isTrial;
      if (currentSessionId) {
        payload.sessionId = currentSessionId;
      }

      // Build headers - cookie handles auth automatically
      const headers = getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.SESSIONS.INSERT(userEmail), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error('Failed to save session');
      }

      await response.json();

      // Clear credits cache and dispatch event to refresh credits in header
      clearCreditsCache();
      window.dispatchEvent(new CustomEvent('creditsUpdated'));

      // Stop recording if active
      if (isRecording) {
        stopRecording();
      }

      // Clear session storage
      localStorage.removeItem('session_end_time');
      localStorage.removeItem('session_active');
      localStorage.removeItem('interview_config');

      // Set trial cooldown timestamp when trial ends
      if (isTrial) {
        localStorage.setItem('bk_last_trial_ended_at', String(Date.now()));
      }

      // Small delay to allow credits event to propagate before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate back to sessions
      navigate('/sessions');
    } catch (error) {
      console.error('Error exiting session:', error);
      setError('Failed to save session: ' + error.message);
      
      // Stop recording if active
      if (isRecording) {
        stopRecording();
      }
      
      // For trial sessions or if there's an error, still navigate away after a short delay
      setTimeout(() => {
        localStorage.removeItem('session_end_time');
        localStorage.removeItem('session_active');
        localStorage.removeItem('interview_config');
        navigate('/sessions');
      }, 1000);
    } finally {
      isExitingRef.current = false;
    }
  };

  const requestExit = (intent = 'manual') => {
    // Trials: no credits, no popup
    if (isTrial) {
      exitSession();
      return;
    }
    setExitIntent(intent);
    setShowExitConfirm(true);
  };
  const handleConfirmExit = async () => {

    setShowExitConfirm(false);

    // For reload specifically, send the exit API before reloading
    if (exitIntent === 'reload') {

      // Use the full exitSession flow which handles all API calls properly
      await exitSession();

      // Allow the reload
      allowUnloadRef.current = true;

      window.location.reload();
      return;
    }

    // For back button, process exit and navigate back
    if (exitIntent === 'back-button') {

      // Use the full exitSession flow
      await exitSession();

      // Allow unload for navigation
      allowUnloadRef.current = true;

      navigate('/sessions');
      return;
    }

    // For other exits (browser close, manual exit), process normally

    await exitSession();

    allowUnloadRef.current = true;

    if (exitIntent === 'browser-close') {
      window.close();
    }

    setExitIntent(null);
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
    setExitIntent(null);
    allowUnloadRef.current = false;
  };

  // State to track if initial auth check is complete
  const [authReady, setAuthReady] = useState(false);
  const initializationDoneRef = useRef(false);

  // Auth check effect - runs first
  useEffect(() => {
    // CRITICAL: Wait for auth hydration before making routing decisions
    // This fixes the production bug where users are redirected to login despite valid tokens
    
    if (!isAuthenticated()) {
      // Double-check after a brief moment to handle production hydration timing
      const recheckTimeout = setTimeout(() => {
        if (!isAuthenticated()) {
          navigate('/signup');
        } else {
          // Auth became valid after hydration
          setAuthReady(true);
        }
      }, 100);
      return () => clearTimeout(recheckTimeout);
    } else {
      // Auth is already valid
      setAuthReady(true);
    }
  }, [navigate]);

  // Main initialization effect - runs after auth is ready
  useEffect(() => {
    if (!authReady || initializationDoneRef.current) return;
    initializationDoneRef.current = true;

    // Check if user came through proper flow (has interview_config)
    // If not, redirect to sessions page to start properly
    const interviewConfig = localStorage.getItem('interview_config');
    if (!interviewConfig || interviewConfig === '{}') {
      // console.log('⚠️ No interview configuration found - redirecting to sessions page');
      navigate('/sessions');
      return;
    }

    // Remove legacy resume cache key on load to avoid duplicates
    if (localStorage.getItem(LEGACY_RESUME_KEY)) {
      localStorage.removeItem(LEGACY_RESUME_KEY);
    }

    // Get session configuration
    const sessionConfig = JSON.parse(interviewConfig);
    const resumeId = sessionConfig.resumeId ? Number(sessionConfig.resumeId) : null;
    const isTrialSession = sessionConfig.isTrial === true;
    setIsTrial(isTrialSession);
    creditsDeductedCountRef.current = 0;
    totalCreditsUsedRef.current = 0;
    
    // Clear old session ID when starting a new session
    // This ensures insertSession() will be called for the new session
    localStorage.removeItem('current_session_id');
    setCurrentSessionId(null);
    
    // Fetch user credits to determine session duration
    const fetchCreditsAndSetTimer = async () => {
      try {
        const data = await fetchCreditsCached();
        const totalCredits = data.totalCredits || 0;
        setUserCredits(totalCredits);
      // console.log('💰 User credits:', totalCredits);
        
        // Calculate max session duration based on credits (1 credit = 60 minutes)
        const maxMinutes = Math.floor(totalCredits * 2 * 30);
        const maxSeconds = maxMinutes * 60;
        setMaxSessionDuration(maxSeconds);
        

      } catch (error) {
        console.error('Error fetching credits:', error);
      }
    };
    
    // Set timer based on session type and credits
    if (isTrialSession) {
      setTimeRemaining(600); // 10 minutes for trial
    } else {
      fetchCreditsAndSetTimer();
      // Check credits immediately for actual sessions
      checkCreditsForActualSession();
    }

    // Check if there's an active session or cooldown (only for paid sessions, not trial)
    const sessionEndTime = localStorage.getItem('session_end_time');
    const cooldownEnd = localStorage.getItem('cooldown_end');
    
    if (cooldownEnd && !isTrial) {
      const remainingCooldown = parseInt(cooldownEnd) - Date.now();
      if (remainingCooldown > 0) {
        alert(`Please wait ${Math.ceil(remainingCooldown / 1000)} seconds before starting a new session.`);
        navigate('/sessions');
        return;
      } else {
        localStorage.removeItem('cooldown_end');
      }
    }

    if (sessionEndTime) {
      const remaining = Math.floor((parseInt(sessionEndTime) - Date.now()) / 1000);
      if (remaining > 0) {
        setTimeRemaining(remaining);
      } else {
        handleSessionEnd();
      }
    }

    // Initialize session start time AFTER isTrial is set
    // Use setTimeout to ensure isTrial state update has been processed
    setTimeout(() => {
      setSessionStartTime(new Date());
    }, 0);

    // Fetch resume data once at session start
    const fetchResumeData = async () => {
      const targetResumeId = resumeId || null;

      // Skip duplicate fetches for the same resumeId
      if (resumeFetchInFlightRef.current) {
      // console.log('Resume fetch already in progress, skipping duplicate call');
        return;
      }
      if (lastResumeIdFetchedRef.current === targetResumeId) {
      // console.log('Resume already fetched for id', targetResumeId);
        return;
      }

      // Use cached response if available for this resumeId
      try {
        if (localStorage.getItem(LEGACY_RESUME_KEY)) {
          localStorage.removeItem(LEGACY_RESUME_KEY);
        }
        const rawCache = localStorage.getItem(RESUME_TEXT_CACHE_KEY);
        const cached = rawCache ? JSON.parse(rawCache) : null;
        if (cached?.data && (resumeId === null || cached.resumeId === resumeId)) {
          if (cached.data.text) {
            localStorage.setItem('resume_content', cached.data.text);
          }
          setCachedResumeData(cached.data);
          lastResumeIdFetchedRef.current = targetResumeId;
          return;
        }
      } catch (cacheErr) {
        console.warn('Failed to parse resume text cache', cacheErr);
      }

      try {
        resumeFetchInFlightRef.current = true;

        const headers = getAuthHeaders();
        
        const response = await fetch(`${BACKEND_URL}/api/resumes/text${resumeId ? `?resumeId=${encodeURIComponent(resumeId)}` : ''}`, {
          method: 'GET',
          credentials: 'include',
          headers
        });

        if (response.ok) {
          const data = await response.json();
      // console.log('Resume data fetched:', data);
          
          // Extract structured data from text if available
          if (data.text && !data.programming_languages && !data.total_experience_years) {
            // Extract programming languages from text
            const langMatches = data.text.match(/(?:programming languages?|skills?|technologies?)\s*:?\s*([^\n]+)/i);
            const languages = langMatches ? langMatches[1].split(/[,;]/).map(l => l.trim()).filter(Boolean) : [];
            
            // Extract years of experience from text
            const expMatches = data.text.match(/(\d+(?:\.\d+)?)\s*(?:\+)?\s*years?\s*(?:of\s*)?(?:experience|exp)/i);
            const experience = expMatches ? parseFloat(expMatches[1]) : 0;
            
            // Extract name from text
            const nameMatches = data.text.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/m);
            const name = nameMatches ? nameMatches[1] : '';
            
            // Merge extracted data with API response
            data.programming_languages = languages;
            data.total_experience_years = experience;
            data.name = name || data.name || '';
            
            // console.log('Extracted from text:', {
            //   name,
            //   programming_languages: languages,
            //   total_experience_years: experience
            // });
          }
          
          // console.log('Final resume data:', data);
          if (data.text) {
            localStorage.setItem('resume_content', data.text);
          }
          localStorage.removeItem(LEGACY_RESUME_KEY);
          localStorage.setItem(RESUME_TEXT_CACHE_KEY, JSON.stringify({
            resumeId: resumeId || null,
            data,
            fetchedAt: Date.now()
          }));
          setCachedResumeData(data);
          lastResumeIdFetchedRef.current = targetResumeId;
        } else {
          console.warn('Resume fetch failed:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching resume:', error);
      } finally {
        resumeFetchInFlightRef.current = false;
      }
    };

    fetchResumeData();

    // Debug: Log available speech recognition APIs
    // console.log('Speech APIs available:', {
    //   SpeechRecognition: !!window.SpeechRecognition,
    //   webkitSpeechRecognition: !!window.webkitSpeechRecognition
    // });

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('SpeechRecognition API not available in this environment. In Electron use a recent Chromium build.');
      setError('Speech Recognition not available in this browser/environment');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true; // keep listening until stopped
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      // Skip Web Speech API results when Speechmatics is active to prevent duplicate words
      if (speechmaticsActiveRef.current) return;

      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) {
          // append final transcript to the accumulated ref
          finalTranscriptRef.current = (finalTranscriptRef.current + ' ' + res[0].transcript).trim();
        } else {
          interim += res[0].transcript;
        }
      }

      // Compose display: accumulated finals + current interim (if any)
      const display = interim ? `${finalTranscriptRef.current} ${interim}`.trim() : finalTranscriptRef.current;
      setTranscript(display);
    };

    recognition.onend = () => {
      // When recognition ends naturally, automatically restart if still recording
      // console.log('🔄 Recognition ended, isRecordingRef.current:', isRecordingRef.current, 'error:', recognitionErrorRef.current);
      
      // Clear any existing restart timeout
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      
      // Only restart if we're still recording and it wasn't due to a critical error
      if (recognitionRef.current && isRecordingRef.current && !recognitionErrorRef.current) {
        // Use a shorter delay for faster restart
        restartTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current && isRecordingRef.current) {
            try {
      // console.log('🔄 Auto-restarting recognition...');
              recognitionRef.current.start();
            } catch (err) {
      // console.log('⚠️ Could not restart recognition:', err);
              // Only stop if it's a real error, not "already started"
              if (err.message && !err.message.includes('already started')) {
                setIsRecording(false);
                isRecordingRef.current = false;
              }
            }
          }
        }, 100); // Reduced from 300ms to 100ms for faster restart
      }
      
      // Reset error flag for next attempt
      recognitionErrorRef.current = false;
    };

    recognition.onerror = (e) => {
      // Log detailed error info
      console.error('Speech recognition error:', {
        error: e,
        errorName: e.error,
        message: e.message,
        timeStamp: e.timeStamp
      });
      
      // Handle different error types
      switch (e.error) {
        case 'no-speech':
          // Don't show error for no-speech, just log it and let it restart
      // console.log('No speech detected, recognition will restart automatically');
          // Don't set error flag - allow restart
          recognitionErrorRef.current = false;
          break;
        case 'audio-capture':
          setError('Microphone not found or not accessible. Please check your mic permissions.');
          setIsRecording(false);
          isRecordingRef.current = false;
          recognitionErrorRef.current = true;
          break;
        case 'not-allowed':
          setError('Microphone access was denied. Please allow microphone access and try again.');
          setIsRecording(false);
          isRecordingRef.current = false;
          recognitionErrorRef.current = true;
          break;
        case 'network':
          setError('Network error occurred. Please check your internet connection.');
          setIsRecording(false);
          isRecordingRef.current = false;
          recognitionErrorRef.current = true;
          break;
        case 'aborted':
          // Recognition was aborted, don't show error
      // console.log('Recognition aborted');
          recognitionErrorRef.current = false;
          break;
        default:
          setError(`Speech recognition error: ${e.error || e.message || 'Unknown error'}`);
          setIsRecording(false);
          isRecordingRef.current = false;
          recognitionErrorRef.current = true;
      }
    };

    recognitionRef.current = recognition;
      // console.log('Speech recognition initialized successfully:', !!recognitionRef.current);

    return () => {
      try {
        recognition.stop();
      } catch (e) {
        // ignore
      }
      
      // Cleanup screen sharing on unmount
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenStream, authReady, navigate]);

  // Sync Speechmatics state with component state
  useEffect(() => {
    setIsRecording(isSpeechmaticsRecording);
    isRecordingRef.current = isSpeechmaticsRecording;
    speechmaticsActiveRef.current = isSpeechmaticsRecording;
  }, [isSpeechmaticsRecording]);

  useEffect(() => {
    setTranscript(speechmaticsTranscript);
    finalTranscriptRef.current = speechmaticsTranscript;
  }, [speechmaticsTranscript]);

  useEffect(() => {
    if (speechmaticsError) {
      setError(speechmaticsError);
    }
  }, [speechmaticsError]);

  // Initialize session on mount
  useEffect(() => {
    // Only initialize session once when component mounts and session is active (skip for trial)
    if (sessionActive && sessionStartTime && !currentSessionId && !isTrial) {
      // console.log('🔵 Initializing session for the first time...');
      insertSession();
    } else if (isTrial && sessionStartTime) {
      // console.log('⏭️ Trial session detected - skipping session initialization API call');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStartTime, isTrial]);

  // Auto-start mic recording when session begins (both trial and paid)
  const autoRecordTriggeredRef = useRef(false);
  useEffect(() => {
    if (sessionStartTime && !autoRecordTriggeredRef.current && !isRecording) {
      autoRecordTriggeredRef.current = true;
      // Delay to let insertSession() complete (so sessionId is in localStorage for WebSocket URL)
      const timer = setTimeout(() => {
        console.log('🎤 Auto-starting mic recording for session...');
        startRecording();
      }, 2000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStartTime]);

  // Set up heartbeat interval (3 minutes)
  useEffect(() => {
    // Only set up heartbeat if we have a sessionId and session is active
    if (sessionActive && currentSessionId && !isTrial) {
      // console.log('Setting up 3-minute heartbeat interval for session', currentSessionId);
      
      // Send heartbeat every 3 minutes
      heartbeatIntervalRef.current = setInterval(() => {
      // console.log('3-minute interval reached, sending heartbeat...');
        sessionHeartbeat();
      }, 3 * 60 * 1000); // 3 minutes in milliseconds
    }

    // Cleanup interval on unmount or when session ends
    return () => {
      if (heartbeatIntervalRef.current) {
      // console.log('Clearing heartbeat interval');
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, currentSessionId]);

  // Auto-scroll transcript container when transcript updates
  useEffect(() => {
    if (autoScroll && transcript) {
      const transcriptElement = document.getElementById('transcript');
      if (transcriptElement) {
        transcriptElement.scrollTop = transcriptElement.scrollHeight;
      }
    }
  }, [transcript, autoScroll]);

  // Auto-scroll right chat panel to the newest assistant message (question start), then stop
  useEffect(() => {
    if (!autoScroll) return;
    if (!messages || messages.length === 0) return;

    // Find the latest assistant message (AI answer)
    const lastAssistantIndex = [...messages]
      .map((m, idx) => ({ idx, role: m.role }))
      .filter(m => m.role === 'assistant')
      .map(m => m.idx)
      .pop();

    // Only scroll when a new assistant message appears
    if (lastAssistantIndex !== undefined && lastAssistantIndex !== lastAssistantIndexScrolledRef.current) {
      const targetEl = messageRefs.current[lastAssistantIndex];
      if (targetEl) {
        requestAnimationFrame(() => {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          lastAssistantIndexScrolledRef.current = lastAssistantIndex;
        });
      }
    }
  }, [messages, autoScroll]);

  // Append assistant reply into right panel when available
  useEffect(() => {
    if (chatResponse) {
      setMessages((m) => [...m, { role: 'assistant', text: chatResponse }]);
    }
  }, [chatResponse]);

  // Handle screen stream attachment to video element
  useEffect(() => {
    if (screenStream && videoRef.current) {
      // console.log('Attaching stream to video element', {
      //   stream: screenStream,
      //   videoElement: videoRef.current,
      //   tracks: screenStream.getTracks(),
      //   active: screenStream.active
      // });
      videoRef.current.srcObject = screenStream;
      videoRef.current.play()
        .then(() => {}) // console.log('Video playing successfully'))
        .catch(err => {
          console.error('Error playing video:', err);
        });
      
      // Add visibility change handler to refresh video when tab becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden && screenStream && videoRef.current) {
          // Check if the stream is still active
          const videoTrack = screenStream.getVideoTracks()[0];
          if (!videoTrack || videoTrack.readyState === 'ended' || !screenStream.active) {
            // Stream ended while we were away - clean up and notify user
            console.log('Screen share ended while tab was in background');
            setScreenStream(null);
            preventTabSwitchRef.current = false;
            setError('Screen share ended. Please share your screen again to continue capturing the interview.');
            return;
          }
          
          // console.log('Tab became visible, refreshing video stream');
          // Force refresh the video element
          const currentStream = videoRef.current.srcObject;
          if (currentStream === screenStream) {
            videoRef.current.srcObject = null;
            setTimeout(() => {
              if (videoRef.current && screenStream && screenStream.active) {
                videoRef.current.srcObject = screenStream;
                videoRef.current.play().catch(err => console.error('Error replaying video:', err));
              }
            }, 100);
          }
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else if (!screenStream && videoRef.current) {
      // console.log('Clearing video element');
      videoRef.current.srcObject = null;
    }
  }, [screenStream]);

  // Separate function for screen sharing
  const startScreenSharing = async () => {
    // Store reference to current window/tab

    
    try {
      // console.log('Requesting screen sharing...');
      
      // Enable tab switch prevention
      preventTabSwitchRef.current = true;
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          // Allow all display surfaces (window, monitor, browser) for better compatibility
          displaySurface: 'window' // Prefer window sharing (Teams/Zoom window stays active when switching)
        },
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        },
        preferCurrentTab: false,
        surfaceSwitching: 'include',
        selfBrowserSurface: 'exclude',
        systemAudio: 'include'
      });
      
      setScreenStream(stream);
      // console.log('Screen sharing started successfully', stream);
      // console.log('Screen stream tracks:', {
      //   videoTracks: stream.getVideoTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled, readyState: t.readyState })),
      //   audioTracks: stream.getAudioTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled, readyState: t.readyState }))
      // });
      
      // Warn if no audio tracks
      if (stream.getAudioTracks().length === 0) {
        console.warn('⚠️ No audio tracks in screen share!');
        console.warn('💡 Did you check "Also share tab audio" in the browser dialog?');
        setError('Screen shared without audio. To capture audio, stop sharing and share again with "Also share tab audio" checked.');
      } else {
      // console.log('✅ Screen share includes audio tracks');
        setError(null);
      }
      
      // Set up stream end detection
      stream.getVideoTracks()[0].addEventListener('ended', () => {
      // console.log('Screen sharing ended by user');
        setScreenStream(null);
        preventTabSwitchRef.current = false;
      });
      
      // IMMEDIATE focus back - multiple synchronous attempts
      bringBackFocus();
      bringBackFocus();
      bringBackFocus();
      
      // Use multiple requestAnimationFrame for immediate next frames
      for (let i = 0; i < 20; i++) {
        requestAnimationFrame(() => bringBackFocus());
      }
      
      // Very aggressive refocus with many short intervals
      const refocusAttempts = [
        0, 1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 100,
        120, 140, 160, 180, 200, 220, 250, 300, 350, 400, 450, 500,
        600, 700, 800, 900, 1000, 1200, 1500, 2000, 2500, 3000
      ];
      
      refocusAttempts.forEach(delay => {
        setTimeout(() => {
          bringBackFocus();
        }, delay);
      });
      
      // Continuous focus monitoring for the first 5 seconds
      const focusMonitorInterval = setInterval(() => {
        if (document.hidden || !document.hasFocus()) {
      // console.log('Window lost focus, bringing it back...');
          bringBackFocus();
        }
      }, 100); // Check every 100ms
      
      // Stop monitoring after 5 seconds
      setTimeout(() => {
        clearInterval(focusMonitorInterval);
      }, 5000);
      
      // Display the shared screen in the video element
      if (videoRef.current) {
      // console.log('Setting video srcObject and playing...');
        videoRef.current.srcObject = stream;
        
        // Ensure video plays immediately
        videoRef.current.play()
          .then(() => {
            // console.log('✅ Video element is playing the screen share');
            // console.log('Video tracks:', stream.getVideoTracks().map(t => ({
            //   id: t.id,
            //   label: t.label,
            //   enabled: t.enabled,
            //   muted: t.muted,
            //   readyState: t.readyState
            // })));
          })
          .catch(err => {
            console.error('❌ Error playing video:', err);
            // Try again after a short delay
            setTimeout(() => {
              if (videoRef.current && videoRef.current.srcObject) {
                videoRef.current.play().catch(e => console.error('Retry failed:', e));
              }
            }, 500);
          });
      }
    } catch (err) {
      console.error('Screen sharing error:', err);
      preventTabSwitchRef.current = false;
      if (err.name === 'NotAllowedError') {
        setError('Screen sharing was denied. Please allow screen sharing to continue.');
      } else {
        setError('Failed to start screen sharing: ' + err.message);
      }
    }
  };

  // Function for starting microphone/speech recognition with Speechmatics
  const startRecording = async () => {
    setError(null);
    
    // Check microphone permission
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'microphone' });
        if (result.state === 'denied') {
          setError('Microphone access is blocked. Please allow access in your browser/system settings.');
          return;
        }
      } catch (e) {
        // Continue anyway as not all browsers support permission check
      }
    }

    try {
      await startSpeechmaticsRecording(screenStream);
    } catch (err) {
      console.error('❌ Failed to start Speechmatics recording:', err);
      setError(err.message || 'Failed to start recording');
    }
  };

  // Stop only speech recognition (keep screen sharing alive)
  const stopListening = () => {
    stopSpeechmaticsRecording();
  };

  // Stop screen sharing only
  const stopScreenSharing = () => {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      preventTabSwitchRef.current = false;
      // console.log('Screen sharing stopped');
      
      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  // Stop everything (used by session end/cleanup paths)
  const stopRecording = () => {
    stopListening();
    stopScreenSharing();
  };

  const clearTranscript = () => {
    // Clear hook's transcript state and refs
    clearSpeechmaticsTranscript();
    // Clear local state and refs
    finalTranscriptRef.current = '';
    setTranscript('');
    console.log('🧹 Transcript cleared');
  };

  // Auto-start screen sharing if coming from Start Interview button
  useEffect(() => {
    if (!autoStartTriggeredRef.current) {
      // Check if screen sharing was skipped (mobile device)
      const screenShareSkipped = localStorage.getItem('screen_share_skipped');
      
      if (screenShareSkipped === 'true') {
        localStorage.removeItem('screen_share_skipped');
        autoStartTriggeredRef.current = true;
        // On mobile, skip screen sharing and continue without it
        return;
      }
      
      // Check if we have a pending screen stream from the modal
      const screenShareRequested = localStorage.getItem('screen_share_requested');
      const pendingStream = window.pendingScreenStream;
      
      if (screenShareRequested === 'true' && pendingStream) {
        localStorage.removeItem('screen_share_requested');
        autoStartTriggeredRef.current = true;
        
      // console.log('Picking up screen stream from modal');
        setScreenStream(pendingStream);
        preventTabSwitchRef.current = true;
        
        // Set up stream end detection
        pendingStream.getVideoTracks()[0].addEventListener('ended', () => {
      // console.log('Screen sharing ended by user');
          setScreenStream(null);
          preventTabSwitchRef.current = false;
        });
        
        // SUPER AGGRESSIVE immediate focus restoration
        for (let i = 0; i < 15; i++) {
          bringBackFocus();
        }
        
        // Aggressive refocus with requestAnimationFrame
        for (let i = 0; i < 40; i++) {
          requestAnimationFrame(() => bringBackFocus());
        }
        
        // Very frequent refocus attempts (every 5ms for first 1000ms)
        const intervals = [];
        for (let i = 0; i <= 1000; i += 5) {
          intervals.push(i);
        }
        // Then less frequent for next 2 seconds
        for (let i = 1000; i <= 3000; i += 100) {
          intervals.push(i);
        }
        
        intervals.forEach(delay => {
          setTimeout(() => {
            bringBackFocus();
          }, delay);
        });
        
        // Continuous focus monitoring for the first 3 seconds
        const focusMonitorInterval = setInterval(() => {
          if (document.hidden || !document.hasFocus()) {
      // console.log('Window lost focus, bringing it back immediately...');
            bringBackFocus();
          }
        }, 50); // Check every 50ms
        
        // Stop monitoring after 3 seconds
        setTimeout(() => {
          clearInterval(focusMonitorInterval);
        }, 3000);
        
        // Clean up the global reference
        window.pendingScreenStream = null;
      } else if (screenShareRequested === 'true') {
        // Fallback: if stream wasn't passed, request it again
        localStorage.removeItem('screen_share_requested');
        autoStartTriggeredRef.current = true;
        setTimeout(() => {
          startScreenSharing();
        }, 500);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to format AI response text for better readability
  const formatAIResponse = (text) => {
    if (!text) return '';
    
    let formatted = text;

    // Render fenced code blocks ```lang\ncode```
    formatted = formatted.replace(/```(\w+)?\s*([\s\S]*?)```/g, (_, lang, code) => {
      const escaped = (code || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const block = escaped.trim().replace(/\n/g, '<br/>');
      const langLabel = lang ? `<div class="code-lang">${lang}</div>` : '';
      return `<div class="code-block">${langLabel}<pre>${block}</pre></div>`;
    });
    
    // Format numbered lists (1., 2., etc.)
    formatted = formatted.replace(/(\d+)\.\s+\*\*/g, '<div class="formatted-number">$1.</div><strong>');
    formatted = formatted.replace(/(\d+)\.\s+/g, '<div class="formatted-number">$1.</div>');
    
    // Format bullet points: • at start of line or after newline
    formatted = formatted.replace(/^•\s*/gm, '<div class="formatted-bullet">•</div>');
    formatted = formatted.replace(/\n•\s*/g, '<br/><div class="formatted-bullet">•</div>');
    // Also handle - bullets
    formatted = formatted.replace(/^-\s+/gm, '<div class="formatted-bullet">•</div>');
    formatted = formatted.replace(/\n-\s+/g, '<br/><div class="formatted-bullet">•</div>');
    
    // Format bold text (**text**)
    // Key-point headings: short bold phrases (1-6 words ending with ':') get special highlight
    formatted = formatted.replace(/\*\*((?:\S+\s*){1,6}?:\s*)\*\*/g, '<strong class="key-point">$1</strong>');
    // Regular bold text stays plain bold
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Format italic text (*text*)
    formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Format inline code
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
    
    // Normalize newlines to avoid large vertical gaps
    formatted = formatted.replace(/\n\n+/g, '<br/><br/>');
    formatted = formatted.replace(/\n/g, '<br/>');
    
    // Highlight specific section labels only
    const sectionLabels = ['Answer:', 'Key Points:', 'Code:', 'Explanation:', 'Key Differences:', 'Summary:', 'Example:', 'Note:', 'Important:', 'Steps:', 'Solution:', 'Output:', 'Syntax:', 'Definition:', 'Conclusion:'];
    sectionLabels.forEach(label => {
      // Match the label with optional emoji prefix (1-3 non-ASCII chars)
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?:[^\\x00-\\x7F\\s]{1,3}\\s+)?${escapedLabel}`, 'gi');
      formatted = formatted.replace(regex, (match) => {
        return `<span class="section-label">${match}</span>`;
      });
    });
    
    return formatted;
  };

  const sendTyped = async () => {
    const text = typed.trim();
    if (!text) return;
    
    // Prevent duplicate requests
    if (sendingRequestRef.current) {
      // console.log('Request already in progress, ignoring duplicate call');
      return;
    }
    
    sendingRequestRef.current = true;
    
    // Add user message
    setMessages((m) => [...m, { role: 'user', text }]);
    setTyped('');
    
    // Add an empty assistant message that we'll update with streaming chunks
    let assistantMsgId = Date.now(); // Unique ID for this message
    setMessages((m) => [...m, { role: 'assistant', text: '', streaming: true, id: assistantMsgId }]);
    
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      // console.log('Sending manual message:', text);
      

      
      // Single source for backend URL via shared config
      const backendUrl = BACKEND_URL;
      const apiUrl = `${backendUrl}/api/chat/response`;
      // console.log('Calling backend API:', apiUrl);
      
      // Build conversation history (exclude the empty assistant message we just added)
      const conversationHistory = messages
        .filter(msg => msg.id !== assistantMsgId) // Exclude the placeholder message
        .map(msg => ({
          role: msg.role,
          content: msg.text
        }));
      
      // Add current user message
      conversationHistory.push({
        role: 'user',
        content: text
      });
      
      const sessionConfig = JSON.parse(localStorage.getItem('interview_config') || '{}');
      const modelMapping = {
        'GPT-4.1 (Smarter)': 'gpt-4.1',
        'GPT-4.0 (Faster)': 'gpt-4o',
        'GPT-3.5 (Budget)': 'gpt-3.5-turbo'
      };
      const selectedModel = sessionConfig.aiModel || 'GPT-4.0 (Faster)';
      const gptModel = modelMapping[selectedModel] || selectedModel || 'gpt-4o';
      const apiModel = normalizeModel(gptModel);

      const requestPayload = {
        model: apiModel,
        input: text // Keep input field for backward compatibility
      };
      
      // console.log('Request payload:', requestPayload);
      
      let res;
      try {
      // console.log('Sending fetch request...');
        res = await fetch(apiUrl, {
          method: 'POST',
          credentials: 'include', // Send cookies for cookie-based auth
          headers: getAuthHeaders({ 'Accept': 'text/event-stream' }),
          body: JSON.stringify(requestPayload),
        });
      // console.log('Fetch completed successfully, status:', res.status);
      } catch (fetchError) {
        console.error('Fetch failed completely:', fetchError);
        console.error('Error name:', fetchError.name);
        console.error('Error message:', fetchError.message);
        console.error('Error stack:', fetchError.stack);
        throw new Error(`Network request failed: ${fetchError.message}`);
      }
      
      // console.log('Response status:', res.status);
      // console.log('Response headers:', [...res.headers.entries()]);
      
      if (!res.ok) {
        let errorText;
        try {
          errorText = await res.text();
        } catch (e) {
          errorText = 'Could not read error response';
        }
        console.error('Backend API error:', res.status, errorText);
        throw new Error(`Backend error (${res.status}): ${errorText}`);
      }
      
      // Check if response body exists
      if (!res.body) {
        throw new Error('Response body is null - streaming not supported');
      }
      
      // Handle streaming response
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let hasReceivedData = false;
      
      try {
        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError) {
            console.warn('Stream read error (possibly incomplete):', readError);
      // console.log('hasReceivedData:', hasReceivedData, 'accumulatedText length:', accumulatedText?.length || 0);
            // If we've received some data, treat it as a partial success
            if (hasReceivedData && accumulatedText && accumulatedText.length > 0) {
      // console.log('Stream interrupted but partial data received:', accumulatedText.length, 'characters');
              // Update the message one last time before breaking
              setMessages((m) => 
                m.map(msg => 
                  msg.id === assistantMsgId 
                    ? { ...msg, text: accumulatedText, streaming: false }
                    : msg
                )
              );
              break; // Exit the while loop successfully
            }
            throw readError;
          }
          
          const { done, value } = readResult;
          if (done) {
      // console.log('Stream reading complete');
            break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
      // console.log('Received chunk:', chunk);
          
          // Split by newlines and process each line
          const lines = chunk.split('\n');
          
          for (let line of lines) {
            line = line.trim();
            if (line === '') continue;
            
            // Handle both "data: " and "data:data:" prefixes
            if (line.startsWith('data:')) {
              // Remove "data:" prefix (handle multiple occurrences)
              let data = line.replace(/^data:\s*/, '').trim();
              
              // Sometimes backend sends "data:data:" - remove extra prefix
              if (data.startsWith('data:')) {
                data = data.replace(/^data:\s*/, '').trim();
              }
              
              if (data === '[DONE]') {
      // console.log('Stream completed with [DONE]');
                continue;
              }
              
              if (data === '') continue;
              
              try {
                const parsed = JSON.parse(data);
      // console.log('Parsed SSE data:', parsed);
                
                // Check for error messages in the stream
                if (parsed.error || parsed.status === 500) {
                  console.error('Server error in stream:', parsed);
                  if (hasReceivedData && accumulatedText && accumulatedText.length > 0) {
                    // Continue with partial data
      // console.log('Continuing with partial response due to server error');
                    break;
                  }
                  // Don't throw, just log and continue to see if more data comes
                  console.warn('Server error but continuing to read stream');
                  continue;
                }
                
                // Handle different response formats - be flexible with field names
                let deltaText = null;
                
                // Check various possible delta field names
                if (parsed.type === 'response.output_text.delta' || parsed.type === 'response.content_part.delta') {
                  deltaText = parsed.delta || parsed.text || parsed.content;
                } else if (parsed.type === 'response.output_item.done') {
                  // Sometimes the text comes in the "done" event
                  if (parsed.item && parsed.item.content) {
                    deltaText = parsed.item.content;
                  }
                } else if (parsed.text) {
                  // Direct text field
                  deltaText = parsed.text;
                } else if (parsed.content) {
                  // Direct content field
                  deltaText = parsed.content;
                } else if (parsed.delta) {
                  // Direct delta field
                  deltaText = parsed.delta;
                }
                
                // If we found text, add it
                if (deltaText) {
                  accumulatedText += deltaText;
                  hasReceivedData = true;
      // console.log('Added delta text:', deltaText, '| Total length:', accumulatedText.length);
                  
                  // Update the assistant message with accumulated text in real-time
                  setMessages((m) => 
                    m.map(msg => 
                      msg.id === assistantMsgId 
                        ? { ...msg, text: accumulatedText, streaming: true }
                        : msg
                    )
                  );
                } else if (parsed.type === 'response.output_text.done' || parsed.type === 'response.done') {
      // console.log('Response completion event received');
                } else {
      // console.log('Event type without text:', parsed.type);
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', data, '| Error:', e.message);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('Stream processing error:', streamError);
      // console.log('Stream error - hasReceivedData:', hasReceivedData, 'text length:', accumulatedText?.length || 0);
        
        // If we have ANY data, treat it as success (stream might end abruptly from backend)
        if (hasReceivedData && accumulatedText && accumulatedText.length > 0) {
      // console.log('Stream ended with data -', accumulatedText.length, 'characters received');
          // Update message as complete with the data we have
          setMessages((m) => 
            m.map(msg => 
              msg.id === assistantMsgId 
                ? { ...msg, text: accumulatedText, streaming: false }
                : msg
            )
          );
          // Exit successfully - this is normal behavior
      // console.log('Stream completed successfully with full response');
          return;
        } else {
          // No data received at all, this is a real error
          console.error('Stream error with no data received');
          throw streamError;
        }
      }
      
      // Stream completed without errors - mark as complete
      // console.log('Stream read completed normally - text length:', accumulatedText.length);
      setMessages((m) => 
        m.map(msg => 
          msg.id === assistantMsgId 
            ? { ...msg, streaming: false }
            : msg
        )
      );
      
      // console.log('Final response length:', accumulatedText.length, 'characters');
      
    } catch (e) {
      console.error('Error in sendTyped:', e);
      
      // Check if we have any accumulated text despite the error
      let hasPartialData = false;
      setMessages((m) => {
        const msg = m.find(msg => msg.id === assistantMsgId);
        if (msg && msg.text && msg.text.length > 0) {
          hasPartialData = true;
          // Mark as complete even with error if we have data
          return m.map(msg => 
            msg.id === assistantMsgId 
              ? { ...msg, streaming: false }
              : msg
          );
        }
        return m;
      });
      
      // Only show error and remove message if we have no data at all
      if (!hasPartialData) {
        setError(`Error: ${e.message}`);
        setMessages((m) => m.filter(msg => msg.id !== assistantMsgId));
      } else {
      // console.log('Error occurred but keeping partial response');
        // Clear the error since we have partial data
        setError(null);
      }
    } finally {
      setLoading(false);
      sendingRequestRef.current = false; // Reset the flag
    }
  };

  // Allow Enter key to submit typed message like the Send button
  const handleTypedKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTyped();
    }
  };

  const sendTranscript = async () => {
      // console.log('sendTranscript called, transcript:', transcript);
    if (!transcript.trim()) {
      // console.log('Transcript is empty, not sending');
      setError('Please speak something first or type a message');
      return;
    }
    
    const userMessage = transcript.trim();
    
    // Add user message
    setMessages((m) => [...m, { role: 'user', text: userMessage }]);
    
    // Add empty assistant message for streaming
    const assistantMsgId = Date.now();
    setMessages((m) => [...m, { role: 'assistant', text: '', streaming: true, id: assistantMsgId }]);
    
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated()) {
        throw new Error('Authentication not found');
      }

      const sessionConfig = JSON.parse(localStorage.getItem('interview_config') || '{}');
      
      // Map display names to actual model identifiers
      const modelMapping = {
        'GPT-4.1 (Smarter)': 'gpt-4.1-mini',
        'GPT-4.0 (Faster)': 'gpt-4o',
        'GPT-3.5 (Budget)': 'gpt-3.5-turbo'
      };
      
      // Get the selected model from session config
      const selectedModel = sessionConfig.aiModel || 'GPT-4.0 (Faster)';
      
      // Convert display name to actual model identifier, or use as-is if it's already an identifier
      const gptModel = modelMapping[selectedModel] || selectedModel || 'gpt-4o';
      const apiModel = normalizeModel(gptModel);
      
      // console.log('Selected model:', selectedModel, '-> API model:', apiModel);
      
      // Use cached resume data - pass complete resume structure to backend
      // Pass only the cached data without explicitly adding duplicate fields
      const gptResponse = cachedResumeData || {};

      // console.log('Cached resume data:', cachedResumeData);
      // console.log('Extracted gptResponse:', gptResponse);
      // console.log('Interview API payload:', { userMessage, gptModel: apiModel, gptResponse });

      // console.log('🚀 Sending request to:', `${BACKEND_URL}/api/chat/interview`);
      // console.log('📦 Request payload:', JSON.stringify({ userMessage, gptModel, gptResponse }, null, 2));
      
      let response;
      try {
        const headers = getAuthHeaders({ 'Accept': 'text/event-stream' });
        
        response = await fetch(`${BACKEND_URL}/api/chat/interview`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ userMessage, gptModel: apiModel, gptResponse, answerMode: sessionConfig.answerMode || 'medium', sessionId: currentSessionId }),
        });
      } catch (fetchError) {
        console.error('❌ Network fetch failed:', fetchError);
        throw new Error(`Network error: ${fetchError.message}. Please check your internet connection.`);
      }

      // console.log('📡 Response received - Status:', response.status, 'OK:', response.ok);
      // console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      // Check if response body exists
      if (!response.body) {
        console.error('❌ Response body is null');
        throw new Error('Server returned empty response. Streaming not supported or connection issue.');
      }

      // Handle streaming response
      // console.log('✅ Response body available, creating reader...');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = ''; // Buffer for incomplete lines

      // console.log('✅ Starting to read stream from /api/chat/interview...');

      try {
        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError) {
            console.warn('⚠️ Reader.read() threw error:', readError.message, 'Type:', readError.name);
            // SSE streams commonly throw network errors when closing - this is NORMAL
            // As long as we have data, treat this as successful completion
      // console.log('✅ Stream ended with data (this is normal SSE behavior), accumulated:', accumulatedText.length, 'chars');
            // IMPORTANT: Don't break yet - we need to process the buffer first
            // Set readResult to indicate done so we process buffer and exit gracefully
            readResult = { done: true, value: undefined };
          }
          
          const { done, value } = readResult;
          
          if (done) {
      // console.log('✅ Stream done gracefully, final text length:', accumulatedText.length);
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
      // console.log('📦 Raw chunk received:', chunk.substring(0, 200));
          buffer += chunk;

          // Process complete lines from buffer
          const lines = buffer.split('\n');
          
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';
          
          // Track SSE event type from event: lines
          let currentEventType = null;
          
          for (const line of lines) {
            const trimmedLine = line.trim();
            
            // Skip empty lines
            if (!trimmedLine) continue;
            
      // console.log('📝 Processing line:', trimmedLine.substring(0, 150));
            
            // Handle SSE event type lines (event: response.output_text.delta)
            if (trimmedLine.startsWith('event:')) {
              currentEventType = trimmedLine.slice(6).trim();
      // console.log('🎭 SSE Event type:', currentEventType);
              continue;
            }
            
            // Handle Server-Sent Events format (data: ...)
            if (trimmedLine.startsWith('data:')) {
              let data = trimmedLine.slice(5).trim(); // Remove "data:"
              
              // Handle double "data:" prefix (some backends send "data:data:")
              if (data.startsWith('data:')) {
                data = data.slice(5).trim();
      // console.log('🔧 Removed double data: prefix');
              }
              
              // Handle triple "data:" prefix (data:data:data:)
              if (data.startsWith('data:')) {
                data = data.slice(5).trim();
      // console.log('🔧 Removed triple data: prefix');
              }
              
              // Skip empty data, [DONE] marker, or lines with just "data:"
              if (!data || data === '[DONE]' || data === 'data:' || data.includes('[DONE]')) {
                if (data === '[DONE]' || data.includes('[DONE]')) {
      // console.log('✅ Received [DONE] marker - stream ending normally');
                }
                continue;
              }
              
      // console.log('📦 Data to parse:', data.substring(0, 200));
              
              try {
                const parsed = JSON.parse(data);
                
                // Use event type from SSE event: line if available, otherwise from parsed.type
                const eventType = currentEventType || parsed.type;
      // console.log('🔍 Event type:', eventType);
      // console.log('🔍 Full parsed object keys:', Object.keys(parsed));
      // console.log('🔍 Full parsed object:', JSON.stringify(parsed).substring(0, 300));
                
                // Reset current event type after using it
                currentEventType = null;
                
                let content = '';
                
                // **STANDARD OPENAI CHAT COMPLETION STREAMING FORMAT**
                // This is the most common format: { id, choices: [{ delta: { content: "..." } }] }
                if (parsed.choices && Array.isArray(parsed.choices) && parsed.choices.length > 0) {
                  const choice = parsed.choices[0];
                  if (choice.delta && choice.delta.content) {
                    content = choice.delta.content;
      // console.log('✅ Standard OpenAI streaming format - delta content:', content);
                  } else if (choice.message && choice.message.content) {
                    // Final message format
                    content = choice.message.content;
      // console.log('✅ Standard OpenAI final message format - content:', content.substring(0, 100));
                  } else if (choice.finish_reason) {
      // console.log('✅ Stream finished with reason:', choice.finish_reason);
                    continue;
                  }
                }
                
                // If we got content from standard format, use it and skip other checks
                if (content) {
                  accumulatedText += content;
      // console.log('📝 Accumulated so far:', accumulatedText.length, 'chars -', accumulatedText.substring(Math.max(0, accumulatedText.length - 50)));
                  
                  setMessages((m) =>
                    m.map(msg =>
                      msg.id === assistantMsgId ? { ...msg, text: accumulatedText } : msg
                    )
                  );
                  continue;
                }
                
                // Handle different event types based on OpenAI Realtime API format
                if (eventType === 'response.created') {
      // console.log('📌 Response created event');
                  continue;
                }
                
                if (eventType === 'response.done' || eventType === 'response.completed') {
      // console.log('✅ Response done/completed event');
                  // Extract final content from response.done if available
                  if (parsed.response && parsed.response.output) {
                    const outputs = Array.isArray(parsed.response.output) ? parsed.response.output : [parsed.response.output];
                    for (const output of outputs) {
                      if (output.content && Array.isArray(output.content)) {
                        const text = output.content
                          .filter(c => c.type === 'text' && c.text)
                          .map(c => c.text)
                          .join('');
                        if (text && !accumulatedText) {
                          content = text;
                        }
                      }
                    }
                  }
                  continue;
                }
                
                // Handle response.output_item.added - initial item with content
                if (eventType === 'response.output_item.added') {
      // console.log('📌 Output item added');
                  if (parsed.item && parsed.item.content) {
                    if (Array.isArray(parsed.item.content)) {
                      content = parsed.item.content
                        .filter(c => c.type === 'text' && c.text)
                        .map(c => c.text)
                        .join('');
      // console.log('📝 Extracted content from output_item.added:', content.substring(0, 100));
                    }
                  }
                }
                
                // Handle response.output_item.done - final item with complete content
                if (eventType === 'response.output_item.done') {
      // console.log('✅ Output item done');
                  if (parsed.item && parsed.item.content) {
                    if (Array.isArray(parsed.item.content)) {
                      const fullContent = parsed.item.content
                        .filter(c => c.type === 'text' && c.text)
                        .map(c => c.text)
                        .join('');
      // console.log('📝 Full content from output_item.done:', fullContent.substring(0, 100));
                      // Use this content if we haven't accumulated anything yet
                      if (fullContent && accumulatedText.length === 0) {
                        content = fullContent;
                      }
                    }
                  }
                }
                
                // Handle response.content_part.added - streaming content part
                if (eventType === 'response.content_part.added') {
      // console.log('📌 Content part added');
                  if (parsed.part && parsed.part.text) {
                    content = parsed.part.text;
      // console.log('📝 Content from content_part.added:', content.substring(0, 100));
                  }
                }
                
                // Handle response.output_text.delta - streaming text deltas (MOST COMMON)
                if (eventType === 'response.output_text.delta') {
      // console.log('🎯 Found response.output_text.delta event!');
      // console.log('🔍 Delta event structure:', JSON.stringify(parsed).substring(0, 200));
                  
                  // Try multiple paths to find the text content
                  if (parsed.data && typeof parsed.data === 'object') {
      // console.log('🔍 Checking parsed.data:', typeof parsed.data);
                    content = parsed.data.delta || parsed.data.text || parsed.data.content || '';
                    
                    // If data.data exists (nested)
                    if (!content && parsed.data.data) {
                      content = parsed.data.data.delta || parsed.data.data.text || '';
                    }
                  }
                  
                  // Direct fields
                  if (!content) {
                    content = parsed.delta || parsed.text || parsed.content || '';
                  }
                  
                  // Check if delta is an object with a text field
                  if (!content && typeof parsed.delta === 'object') {
                    content = parsed.delta.text || parsed.delta.content || '';
                  }
                  
                  if (content) {
      // console.log('✅ Extracted delta content:', content.substring(0, 100));
                  } else {
                    console.warn('⚠️ Could not extract content from output_text.delta');
                    console.warn('⚠️ Available fields:', Object.keys(parsed));
                  }
                }
                
                // Handle response.text.delta - streaming text deltas (alternative format)
                if (eventType === 'response.text.delta') {
                  content = parsed.delta || parsed.text || '';
      // console.log('📝 Text delta:', content.substring(0, 100));
                }
                
                // Handle response.audio_transcript.delta - audio transcription deltas
                if (eventType === 'response.audio_transcript.delta') {
                  content = parsed.delta || '';
      // console.log('📝 Audio transcript delta:', content.substring(0, 100));
                }
                
                // Skip progress events
                if (eventType === 'response.in_progress') {
      // console.log('⏳ Response in progress...');
                  continue;
                }
                
                // Generic fallbacks for unknown formats
                if (!content && parsed.delta) {
                  content = typeof parsed.delta === 'string' ? parsed.delta : JSON.stringify(parsed.delta);
                }
                if (!content && parsed.content) {
                  content = typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content);
                }
                if (!content && parsed.text) {
                  content = typeof parsed.text === 'string' ? parsed.text : '';
                }
                if (!content && parsed.output) {
                  content = typeof parsed.output === 'string' ? parsed.output : '';
                }
                
                // Update accumulated text and UI
                if (content) {
                  accumulatedText += content;
      // console.log('✅ Added content! Total accumulated:', accumulatedText.length, 'chars');
      // console.log('📄 Latest chunk preview:', content.substring(0, 50));
                  
                  // Update message in real-time
                  setMessages((m) =>
                    m.map(msg =>
                      msg.id === assistantMsgId ? { ...msg, text: accumulatedText } : msg
                    )
                  );
                } else {
      // console.log('⚠️ No content extracted from event type:', parsed.type);
                }
              } catch (parseError) {
                console.error('❌ Parse error:', parseError.message);
                console.error('❌ Failed to parse line:', trimmedLine.substring(0, 200));
              }
            }
            // Handle plain JSON lines (non-SSE format)
            else if (trimmedLine.startsWith('{')) {
              try {
                const parsed = JSON.parse(trimmedLine);
      // console.log('🔍 Plain JSON event type:', parsed.type || 'unknown');
                
                // Extract content from plain JSON using same logic
                let content = '';
                
                if (parsed.type === 'response.output_item.added' && parsed.item) {
                  if (Array.isArray(parsed.item.content)) {
                    content = parsed.item.content
                      .filter(c => c.type === 'text' && c.text)
                      .map(c => c.text)
                      .join('');
                  }
                } else if (parsed.type === 'response.output_item.done' && parsed.item) {
                  if (Array.isArray(parsed.item.content)) {
                    const fullContent = parsed.item.content
                      .filter(c => c.type === 'text' && c.text)
                      .map(c => c.text)
                      .join('');
                    if (fullContent && accumulatedText.length === 0) {
                      content = fullContent;
                    }
                  }
                } else if (parsed.delta || parsed.content || parsed.text || parsed.output) {
                  content = parsed.delta || parsed.content || parsed.text || parsed.output;
                }
                
                if (content) {
                  accumulatedText += content;
      // console.log('✅ Plain JSON content added, total:', accumulatedText.length);
                  
                  setMessages((m) =>
                    m.map(msg =>
                      msg.id === assistantMsgId ? { ...msg, text: accumulatedText } : msg
                    )
                  );
                }
              } catch (parseError) {
                console.error('❌ Plain JSON parse error:', parseError.message);
              }
            }
          }
        }
        
        // Process any remaining buffer content
        if (buffer.trim()) {
      // console.log('📦 Processing remaining buffer:', buffer.length, 'bytes');
      // console.log('📦 Buffer content:', buffer);
          
          // Try to parse remaining buffer as SSE data
          const lines = buffer.split('\n');
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('data:')) {
              let data = trimmedLine.slice(5).trim();
              if (data.startsWith('data:')) {
                data = data.slice(5).trim();
              }
              if (data && data !== '[DONE]') {
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                    const content = parsed.choices[0].delta.content;
                    accumulatedText += content;
      // console.log('✅ Got content from remaining buffer:', content);
                  }
                } catch (e) {
                  console.warn('⚠️ Could not parse remaining buffer line:', e.message);
                }
              }
            }
          }
        }
        
      // console.log('🏁 Stream reading complete. Final accumulated text:', accumulatedText.length, 'characters');
      // console.log('🏁 Final text preview:', accumulatedText.substring(0, 200));
      // console.log('🏁 Final text end:', accumulatedText.substring(Math.max(0, accumulatedText.length - 100)));
        
      } catch (streamError) {
        console.error('❌ Stream error:', streamError);
        console.error('❌ Stream error type:', streamError.name);
        console.error('❌ Stream error message:', streamError.message);
        
        // If we have ANY content, treat stream as successful
        // Backend often closes stream abruptly causing ERR_INCOMPLETE_CHUNKED_ENCODING or TypeError: network error
        if (accumulatedText.length > 0) {
      // console.log('✅ Stream error ignored - we have complete response:', accumulatedText.length, 'chars');
      // console.log('✅ Response preview:', accumulatedText.substring(0, 200));
          // Don't throw - we got the data successfully
        } else {
          // Check if it's just a network error on close (common with SSE)
          if (streamError.name === 'TypeError' && streamError.message.includes('network')) {
      // console.log('⚠️ Network error on stream close (normal for SSE), but no data received');
            // Don't throw - this is often normal
          } else {
            // Only throw for other types of errors with no data
            console.error('❌ Stream error with NO data - this is a real error');
            throw streamError;
          }
        }
      }

      // Mark complete and check if we got any content
      if (accumulatedText.length === 0) {
        console.warn('⚠️ Stream completed but no content received');
        setMessages((m) =>
          m.map(msg =>
            msg.id === assistantMsgId 
              ? { ...msg, text: 'I apologize, but I did not receive a proper response. Could you please try again?', streaming: false } 
              : msg
          )
        );
        setError('No response received from the server. Please try again.');
      } else {
      // console.log('✅ Stream completed successfully with', accumulatedText.length, 'characters');
      // console.log('✅ Final response preview:', accumulatedText.substring(0, 150));
        setMessages((m) =>
          m.map(msg =>
            msg.id === assistantMsgId ? { ...msg, streaming: false } : msg
          )
        );
        // Clear error since we got a successful response
        setError(null);
      }

      // Clear transcript
      setTranscript('');
      finalTranscriptRef.current = '';
      
    } catch (e) {
      console.error('❌ Error in sendTranscript:', e);
      console.error('❌ Error stack:', e.stack);
      const errorMessage = e.message || 'Unknown error occurred. Please try again.';
      
      // Check if we have any accumulated text in the message before showing error
      let hasData = false;
      setMessages((m) => {
        const msg = m.find(msg => msg.id === assistantMsgId);
        if (msg && msg.text && msg.text.length > 0) {
          hasData = true;
          // Mark as complete even with error if we have data
          return m.map(msg => 
            msg.id === assistantMsgId 
              ? { ...msg, streaming: false }
              : msg
          );
        }
        return m;
      });
      
      // Only show error and remove message if we have no data at all
      if (!hasData) {
        setError(errorMessage);
        setMessages((m) =>
          m.map(msg =>
            msg.id === assistantMsgId 
              ? { ...msg, text: `❌ Error: ${errorMessage}\n\nPlease try again or check the console for more details.`, streaming: false } 
              : msg
          )
        );
      } else {
      // console.log('✅ Error occurred but keeping partial response');
        // Clear the error since we have data
        setError(null);
      };
    } finally {
      setLoading(false);
      sendingRequestRef.current = false;
      // console.log('✅ sendTranscript completed, loading:', false);
    }
  };

  // Retrieve prompt for vision API: reuse cached text response or fetch resume text
  const getVisionPrompt = async () => {
    try {
      const cached = localStorage.getItem('last_gpt_response');
      if (cached) return cached;

      const sessionConfig = JSON.parse(localStorage.getItem('interview_config') || '{}');
      const resumeId = sessionConfig.resumeId || sessionConfig.resume_id;
      if (!resumeId) return undefined;

      const headers = getAuthHeaders();

      const res = await fetch(`${BACKEND_URL}/api/resumes/text?resumeId=${resumeId}`, {
        method: 'GET',
        credentials: 'include',
        headers
      });

      if (!res.ok) {
        console.warn('Vision prompt fetch failed with status', res.status);
        return undefined;
      }

      const data = await res.json();
      const rawPrompt = data.gptResponse || data.gptresponse || data.gpt_response || data;
      const promptString = typeof rawPrompt === 'string' ? rawPrompt : JSON.stringify(rawPrompt);

      if (promptString) {
        try { localStorage.setItem('last_gpt_response', promptString); } catch (storageErr) { /* ignore */ }
        return promptString;
      }
    } catch (err) {
      console.warn('Unable to prepare vision prompt:', err);
    }
    return undefined;
  };

  const analyzeScreen = async () => {
    if (!screenStream || !videoRef.current) {
      setError('Please start screen sharing first');
      return;
    }

    setAnalyzingScreen(true);
    setError(null);

    try {
      // Capture current frame from video
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      
      // Resize image to reduce payload size (max 1280px on longest side)
      const maxDimension = 1280;
      let targetWidth = video.videoWidth;
      let targetHeight = video.videoHeight;
      
      if (targetWidth > maxDimension || targetHeight > maxDimension) {
        if (targetWidth > targetHeight) {
          targetHeight = Math.round((targetHeight / targetWidth) * maxDimension);
          targetWidth = maxDimension;
        } else {
          targetWidth = Math.round((targetWidth / targetHeight) * maxDimension);
          targetHeight = maxDimension;
        }
      }
      
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      // Convert to base64 image (JPEG with 0.7 quality for smaller size)
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      // Pull prompt/context for vision from text/resume API
      const visionPrompt = await getVisionPrompt();
      // Add user message showing screen is being analyzed
      setMessages((m) => [...m, { role: 'user', text: 'Analyzing shared screen...' }]);

      // Add empty assistant message for streaming response
      const assistantMsgId = Date.now();
      setMessages((m) => [...m, { role: 'assistant', text: '', streaming: true, id: assistantMsgId }]);

      const headers = getAuthHeaders({ 'Accept': 'text/event-stream' });
      
      const res = await fetch(`${BACKEND_URL}/api/chat/vision`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          model: 'gpt-4.1-mini',
          imageBase64,
          // Always send prompt field so backend receives context even if empty
          prompt: visionPrompt || '',
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error('Vision API error:', txt);
        throw new Error(`Vision API error: ${txt}`);
      }

      if (!res.body) {
        throw new Error('Vision API did not return a streaming body');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = '';
      let hasData = false;

      // Helper to extract text from chunk structures
      const extractText = (parsed) => {
        // OpenAI-style chat.completion.chunk
        const choice = parsed?.choices?.[0];
        if (choice?.delta?.content) {
          // content is an array of parts
          if (Array.isArray(choice.delta.content)) {
            return choice.delta.content
              .map(part => part?.text || '')
              .join('');
          }
          // content could be string
          if (typeof choice.delta.content === 'string') {
            return choice.delta.content;
          }
        }
        // Some backends might put delta.text directly
        if (choice?.delta?.text) return choice.delta.text;

        // Fallbacks
        if (parsed?.text) return parsed.text;
        if (parsed?.content) return parsed.content;
        if (parsed?.delta) return typeof parsed.delta === 'string' ? parsed.delta : '';
        return '';
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            if (!trimmed.startsWith('data:')) continue;

            let dataPart = trimmed.slice(5).trim();
            if (dataPart === '' || dataPart === '[DONE]') continue;

            // Handle double data: prefix
            if (dataPart.startsWith('data:')) {
              dataPart = dataPart.slice(5).trim();
            }

            try {
              const parsed = JSON.parse(dataPart);
              const deltaText = extractText(parsed);
              if (deltaText) {
                hasData = true;
                accumulatedText += deltaText;
                setMessages((m) =>
                  m.map(msg =>
                    msg.id === assistantMsgId ? { ...msg, text: accumulatedText } : msg
                  )
                );
              }
            } catch (parseErr) {
              console.warn('Failed to parse vision stream chunk:', parseErr);
            }
          }
        }
      } catch (streamErr) {
        console.error('Vision stream error:', streamErr);
        if (!hasData) {
          throw streamErr;
        }
      }

      if (!hasData) {
        throw new Error('No response received from vision stream');
      }

      setMessages((m) =>
        m.map(msg =>
          msg.id === assistantMsgId ? { ...msg, streaming: false } : msg
        )
      );
    } catch (e) {
      console.error('Screen analysis error:', e);
      setError(e.message || 'Error analyzing screen');
      // Remove placeholder assistant message if nothing streamed
      setMessages((m) => m.filter(msg => msg.streaming !== true));
    } finally {
      setAnalyzingScreen(false);
    }
  };

  // Removed: askChatGPT function - app now uses backend API for all OpenAI calls

  return (
    <div className="chat-interface-fullscreen">
      {showExitConfirm && (
        <div className="exit-modal-overlay">
          <div className="exit-modal">
            <div className="exit-modal-title">
              {exitIntent === 'reload' ? '🔄 Refresh Page?' : 
               exitIntent === 'back-button' ? '⬅️ Go Back?' : 
               '❌ Exit Session?'}
            </div>
            <p className="exit-modal-body">
              {exitIntent === 'reload' 
                ? 'Refreshing will end your session and deduct credits based on time used. Are you sure you want to refresh?'
                : exitIntent === 'back-button'
                ? 'Going back will end your session and deduct credits based on time used. Are you sure you want to go back?'
                : 'Exiting will end your session and deduct credits based on time used. Are you sure you want to exit?'
              }
            </p>
            <div className="exit-modal-actions">
              <button className="exit-btn ghost" onClick={handleCancelExit}>
                {exitIntent === 'reload' || exitIntent === 'back-button' ? 'Stay on page' : 'Stay in session'}
              </button>
              <button className="exit-btn danger" onClick={handleConfirmExit}>
                {exitIntent === 'reload' ? 'Refresh page' : 
                 exitIntent === 'back-button' ? 'Go back' : 
                 'Exit session'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Main grid (header removed) */}
      <div className="shell chat-shell">
        <div className="grid">
          {/* Left */}
          <div className="panel">
            <div className="panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {screenStream ? 'Sharing screen' : 'Not sharing'}
                  </div>
                </div>
              </div>
              {screenStream ? (
                <button className="btn btn-accent" onClick={stopScreenSharing}>
                  Stop sharing
                </button>
              ) : (
                <button className="btn btn-accent" onClick={startScreenSharing}>
                  Share screen
                </button>
              )}
            </div>

            <div className="video" style={{ position: 'relative', flexShrink: 0 }}>
              {screenStream ? (
                <>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline
                    muted
                    controls={false}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      backgroundColor: '#000',
                      borderRadius: '12px',
                      display: 'block'
                    }}
                    onLoadedMetadata={(e) => {
                      // console.log('Video metadata loaded:', {
                      //   videoWidth: e.target.videoWidth,
                      //   videoHeight: e.target.videoHeight,
                      //   duration: e.target.duration,
                      //   readyState: e.target.readyState
                      // });
                    }}
                    onError={(e) => {
                      console.error('Video element error:', e);
                    }}
                  />
                  {/* Change Tab control */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    display: 'flex',
                    gap: '8px',
                    zIndex: 10
                  }}>
                    <button
                      onClick={startScreenSharing}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(11, 15, 13, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      🔄 Change Tab
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  color: '#9ca3af',
                  gap: '12px',
                  padding: '16px'
                }}>
                  <div style={{ fontSize: 'clamp(32px, 10vw, 48px)', opacity: 0.5 }}>📹</div>
                  <div style={{ fontSize: 'clamp(14px, 4vw, 16px)', fontWeight: '500', textAlign: 'center' }}>Video / Meet preview</div>
                  <div style={{ fontSize: 'clamp(12px, 3.5vw, 14px)', opacity: 0.7, textAlign: 'center', maxWidth: '90%', padding: '0 8px' }}>
                    Share your screen to start the interview session
                  </div>
                </div>
              )}
            </div>

            {/* Separate Controls Section - Completely outside video */}
            <div style={{ marginBottom: '6px', flexShrink: 0 }}>
              <div style={{ 
                padding: 'clamp(6px, 2vw, 8px)',
                background: 'rgba(11, 15, 13, 0.95)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 'clamp(6px, 2vw, 10px)',
                flexWrap: 'wrap',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                {/* Toggle: External Audio - Use mic to capture audio from external device (phone speaker, laptop, etc.) */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  background: useExternalAudio ? 'rgba(59, 130, 246, 0.15)' : 'rgba(100, 116, 139, 0.15)',
                  border: `1px solid ${useExternalAudio ? 'rgba(59, 130, 246, 0.4)' : 'rgba(148, 163, 184, 0.3)'}`,
                  borderRadius: '6px',
                  cursor: isRecording ? 'not-allowed' : 'pointer',
                  opacity: isRecording ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onClick={() => {
                  if (!isRecording) {
                    const newValue = !useExternalAudio;
                    setUseExternalAudio(newValue);
                    // When external audio is ON, disable the other toggle
                    if (newValue) {
                      setCaptureSystemAudio(false);
                    }
                  }
                }}
                title={isRecording
                  ? "Stop recording to change this setting"
                  : "Use microphone to capture audio from external device (phone on speaker, laptop speakers). No screen share needed."}
                >
                  <div style={{
                    width: '28px',
                    height: '16px',
                    background: useExternalAudio ? '#3b82f6' : '#64748b',
                    borderRadius: '8px',
                    position: 'relative',
                    transition: 'all 0.2s',
                    boxShadow: useExternalAudio ? '0 2px 4px rgba(59, 130, 246, 0.4)' : 'none'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: useExternalAudio ? '14px' : '2px',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
                    }} />
                  </div>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: useExternalAudio ? '#3b82f6' : '#94a3b8',
                    whiteSpace: 'nowrap'
                  }}>
                    {useExternalAudio ? '🎤 Mic' : '🎧 Tab Audio'}
                  </span>
                </div>
                </div>
                
                {!isRecording ? (
                  <button 
                    onClick={startRecording} 
                    style={{
                      background: 'var(--brand-green)',
                      color: 'white',
                      border: '1px solid var(--brand-green-dark)',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(72, 208, 42, 0.3)',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🎤 Connect
                  </button>
                ) : (
                  <>
                    {/* Live indicator */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '6px',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        background: '#ef4444',
                        borderRadius: '50%',
                        boxShadow: '0 0 8px #ef4444',
                        animation: 'blink 1s ease-in-out infinite'
                      }} />
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#ef4444',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Live</span>
                    </div>
                    <button 
                      onClick={stopListening} 
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: '1px solid #dc2626',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ⏹ Stop
                    </button>
                  </>
                )}
                
                <button 
                  onClick={clearTranscript} 
                  title="Clear transcript (Delete key)"
                  style={{
                    background: 'rgba(11, 15, 13, 0.95)',
                    color: '#e5e7eb',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ✕ Clear
                </button>
                
                <button 
                  onClick={analyzeScreen} 
                  disabled={analyzingScreen || !screenStream}
                  style={{ 
                    background: (analyzingScreen || !screenStream) ? '#6b7280' : 'linear-gradient(90deg, #06b6d4, #3b82f6)',
                    color: 'white',
                    border: 'none',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: (analyzingScreen || !screenStream) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    justifyContent: 'center',
                    boxShadow: screenStream ? '0 2px 6px rgba(6, 182, 212, 0.3)' : 'none',
                    opacity: (analyzingScreen || !screenStream) ? 0.5 : 1,
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  title={!screenStream ? 'Share screen to analyze' : 'Analyze the shared screen'}
                >
                  {analyzingScreen ? '⏳...' : '🔍 Analyze'}
                </button>
                
              </div>
            </div>

            <div
              className="transcript"
              id="transcript"
              style={{
                position: 'relative',
                minHeight: '140px',
                flex: '1 1 auto',
                overflow: 'auto',
                marginBottom: '0',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
                {transcript ? (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#e5e7eb', padding: '12px' }}>{transcript}</pre>
                ) : (
                  <div style={{ 
                    color: '#9ca3af', 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px', 
                    textAlign: 'center',
                    gap: '12px',
                    minHeight: '200px'
                  }}>
                    <div style={{ fontSize: '36px', opacity: 0.5 }}>{isRecording ? '🎤' : '🎙️'}</div>
                    <div style={{ fontSize: '16px', fontWeight: '500' }}>
                      {isRecording ? 'Listening to your voice...' : 'Ready to Listen'}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.7, maxWidth: '300px' }}>
                      {isRecording ? 'Start speaking to see your transcript here' : 'Click the "Connect" button above to start listening and capturing your voice'}
                    </div>
                  </div>
                )}
              </div>

              {/* AI Answer button fixed at bottom of transcript */}
              <div style={{ 
                padding: '8px',
                background: 'rgba(11, 15, 13, 0.95)',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex', 
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                <button 
                  onClick={sendTranscript} 
                  disabled={loading} 
                  className="btn btn-primary"
                  style={{
                    background: loading ? '#6b7280' : 'linear-gradient(90deg, #10b981, #059669)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s',
                    minWidth: '120px',
                    whiteSpace: 'nowrap',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.3px'
                  }}
                >
                  {loading ? 'Thinking…' : 'AI Answer'}
                </button>
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="panel">
            <div className="panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="logo">
                  <img
                    src="/bkai_logo.jpeg"
                    alt="BlackKiteAI logo"
                    width="512"
                    height="512"
                    loading="lazy"
                  />
                </div>
                <div>
                  <div style={{ fontWeight: 700 }}>BlackKiteAI</div>
                  <div className="brand-sub">Click "AI Answer" to start</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="chip" style={{ 
                  background: (isTrial && timeRemaining < 60) ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255,255,255,0.06)',
                  color: (isTrial && timeRemaining < 60) ? '#ef4444' : 'var(--muted)',
                  fontWeight: (isTrial && timeRemaining < 60) ? '700' : 'normal'
                }}>
                  {isTrial ? (
                    <>⏱ {formatTime(timeRemaining)} (Trial)</>
                  ) : (
                    <>⏱ {formatTime(elapsedTime)}{maxSessionDuration && ` / ${formatTime(maxSessionDuration)}`}</>
                  )}
                </div>
                {show5MinWarning && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.3))',
                    color: '#fbbf24',
                    fontWeight: '700',
                    fontSize: '15px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    animation: 'blinkWarning 3s ease-in-out, fadeIn 0.3s ease-in',
                    border: '2px solid rgba(251, 191, 36, 0.5)',
                    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>⚠️</span>
                    <span>Your session will end in 5 mins</span>
                  </div>
                )}
                {show30SecWarning && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))',
                    color: '#ef4444',
                    fontWeight: '700',
                    fontSize: '16px',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    animation: 'pulse 1s ease-in-out infinite',
                    border: '2px solid rgba(239, 68, 68, 0.6)',
                    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '24px' }}>🚨</span>
                    <span>Your session will be ending soon!</span>
                  </div>
                )}
                {showLowCreditWarning && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.3))',
                    color: '#fbbf24',
                    fontWeight: '700',
                    fontSize: '15px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    animation: 'blinkWarning 3s ease-in-out, fadeIn 0.3s ease-in',
                    border: '2px solid rgba(251, 191, 36, 0.5)',
                    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>⚠️</span>
                    <span>Only 30 minutes (0.5 credits) left!</span>
                  </div>
                )}
                {showMaxTimeWarning && !isTrial && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))',
                    color: '#ef4444',
                    fontWeight: '700',
                    fontSize: '15px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    animation: isMaxTimeBlinking ? 'blinkWarning 1s ease-in-out infinite' : 'fadeIn 0.2s ease-in',
                    border: '2px solid rgba(239, 68, 68, 0.6)',
                    boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>🚨</span>
                    <span>Your session will end in 5 minutes (max time limit)!</span>
                  </div>
                )}
                <button 
                  onClick={() => requestExit('manual')}
                  className="btn btn-ghost"
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.3)'
                  }}
                >
                  🚪 Exit Session
                </button>
              </div>
            </div>

            <div className="chat" id="chat-list" ref={chatListRef}>
              {messages.length === 0 && (
                <div style={{ color: '#9ca3af', textAlign: 'center', marginTop: 24 }}>No messages yet. Click "AI Answer" to start!</div>
              )}
              {messages.map((m, idx) => {
                // Check if message contains formatted question and answer
                const hasFormattedQA = m.text.includes('❓ Question:') && m.text.includes('💡 Answer:');
                
                if (hasFormattedQA && m.role === 'assistant') {
                  // Split and format question and answer separately
                  const parts = m.text.split('💡 Answer:');
                  const questionPart = parts[0].replace('❓ Question:', '').trim();
                  const answerPart = parts[1]?.trim() || '';
                  const formattedAnswer = formatAIResponse(answerPart);
                  
                  return (
                  <div
                    key={idx}
                    className="msg"
                    ref={(el) => { messageRefs.current[idx] = el; }}
                  >
                      <div className="avatar">AI</div>
                      <div className="bubble" style={{ maxWidth: '100%' }}>
                        <div style={{ 
                          background: 'rgba(59, 130, 246, 0.1)', 
                          padding: '12px', 
                          borderRadius: '8px', 
                          marginBottom: '12px',
                          borderLeft: '3px solid #3b82f6'
                        }}>
                          <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600', marginBottom: '6px' }}>❓ QUESTION</div>
                          <div style={{ color: '#e5e7eb', fontSize: '14px' }}>{questionPart}</div>
                        </div>
                        <div style={{ 
                          padding: '12px', 
                          borderRadius: '8px',
                          borderLeft: '3px solid #10b981'
                        }}>
                          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '600', marginBottom: '6px' }}>💡 ANSWER</div>
                          <div 
                            style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.8' }}
                            dangerouslySetInnerHTML={{ __html: formattedAnswer }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Regular message rendering
                return (
                  <div
                    key={idx}
                    className={`msg ${m.role === 'user' ? 'me' : ''}`}
                    ref={(el) => { messageRefs.current[idx] = el; }}
                  >
                    {m.role !== 'user' && <div className="avatar">AI</div>}
                    <div 
                      className={`bubble ${m.role === 'user' ? 'me' : ''}`}
                      {...(m.role === 'assistant' ? { 
                        dangerouslySetInnerHTML: { __html: formatAIResponse(m.text) },
                        style: { lineHeight: '1.5', position: 'relative', color: '#9ca3af' }
                      } : { children: m.text })}
                    />
                    {m.role === 'assistant' && m.streaming && (
                      <div style={{ 
                        display: 'inline-flex', 
                        gap: '4px', 
                        marginLeft: '8px',
                        alignItems: 'center'
                      }}>
                        <span style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          background: '#10b981',
                          animation: 'pulse 1.4s ease-in-out infinite',
                          animationDelay: '0s'
                        }} />
                        <span style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          background: '#10b981',
                          animation: 'pulse 1.4s ease-in-out infinite',
                          animationDelay: '0.2s'
                        }} />
                        <span style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          background: '#10b981',
                          animation: 'pulse 1.4s ease-in-out infinite',
                          animationDelay: '0.4s'
                        }} />
                      </div>
                    )}
                    {m.role === 'user' && <div className="avatar" style={{ background: '#374151' }}>You</div>}
                  </div>
                );
              })}
            </div>

            <div className="inputbar">
              <input className="textbox" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={handleTypedKeyDown} placeholder="Type a manual message..." />
              <button onClick={sendTyped} className="btn btn-ghost">Send</button>
            </div>
          </div>
        </div>

        {/* Diagnostics row */}
        <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {!window.isSecureContext && <div className="chip" style={{ color: '#b91c1c' }}>Not a secure context</div>}
          {error && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 auto' }}>
              <div className="chip" style={{ color: '#b91c1c' }}>Error: {error}</div>
              {(error.includes('Microphone') || error.includes('denied') || error.includes('permission')) && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#fbbf24',
                  padding: '8px 12px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(251, 191, 36, 0.3)'
                }}>
                  💡 Click the 🔒 lock icon in your browser's address bar and allow microphone access, then refresh the page.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 5-Minute Warning Modal */}
      {showWarningModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            padding: '40px',
            borderRadius: '20px',
            maxWidth: '500px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#fff', marginBottom: '16px', fontSize: '24px' }}>Credits Depleted</h2>
            <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '16px' }}>
              Your credits have been used up. You have 5 minutes remaining in this session.
              Please purchase more credits to continue using the service after this session.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => setShowWarningModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px 32px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Continue Session
              </button>
              <button 
                onClick={() => {
                  setShowWarningModal(false);
                  navigate('/buy-credits');
                }}
                style={{
                  background: 'linear-gradient(90deg, #2563eb, #1d4ed8)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Buy Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session End Modal */}
      {showEndModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            padding: '40px',
            borderRadius: '20px',
            maxWidth: '500px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏱️</div>
            <h2 style={{ color: '#fff', marginBottom: '16px', fontSize: '24px' }}>
              {isTrial ? 'Trial Session Completed' : 'Session Completed'}
            </h2>
            <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '16px' }}>
              {isTrial 
                ? 'Your 10-minute trial session has ended. Please wait 15 minutes before starting a new session.'
                : 'Your session has ended. Thank you for using BlackKiteAI!'
              }
            </p>
            <button 
              onClick={() => {
                setShowEndModal(false);
                navigate('/sessions');
              }}
              style={{
                background: 'linear-gradient(90deg, var(--brand-green), var(--brand-green-dark))',
                color: '#fff',
                border: 'none',
                padding: '12px 32px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              Back to Sessions
            </button>
          </div>
        </div>
      )}

      {/* No Credits Modal - Force Exit */}
      {showNoCreditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #7f1d1d, #991b1b)',
            padding: '40px',
            borderRadius: '20px',
            maxWidth: '500px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
            <h2 style={{ color: '#fff', marginBottom: '16px', fontSize: '24px' }}>No Credits Remaining</h2>
            <p style={{ color: '#fca5a5', marginBottom: '24px', fontSize: '16px' }}>
              Your credits have been depleted. This session will end in 3 seconds.
              Please purchase more credits to continue using the service.
            </p>
            <button 
              onClick={() => {
                setShowNoCreditModal(false);
                navigate('/buy-credits');
              }}
              style={{
                background: 'linear-gradient(90deg, #2563eb, #1d4ed8)',
                color: '#fff',
                border: 'none',
                padding: '12px 32px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              Buy Credits Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatInterface;
